import { useState, useEffect } from "react";
import { supabase } from "./lib/supabase";
import { api } from "./lib/api";
import { subscribeToPush, unsubscribeFromPush, pushSupported, isIOS, isStandalone } from "./lib/push";

const C = {
  orange:"#CF4008", orangeDk:"#A83006", yellow:"#FFD030",
  cream:"#FDF8F2", creamDk:"#EDE4D6", white:"#FFFFFF",
  txtDk:"#1A1208", txtMd:"#8A6848", txtLt:"#BBA080",
  green:"#2D7A4F", greenBg:"#EBF5EE", amber:"#E07010", amberBg:"#FFF4E0",
};

const MOODS=[
  {e:"😔",v:1,c:"#E05050",lbl:"Rough"},
  {e:"😕",v:2,c:"#E08030",lbl:"Down"},
  {e:"😐",v:3,c:"#E0B800",lbl:"Okay"},
  {e:"🙂",v:4,c:"#80C030",lbl:"Good"},
  {e:"😁",v:5,c:"#30A860",lbl:"Great"},
];
const mc=(v,mono)=>mono?(YELLOW_SCALE[v-1]??C.yellow):(MOODS[v-1]?.c??"#CCC");
const me=v=>MOODS[v-1]?.e??"";
const ml=v=>MOODS[v-1]?.lbl??"";

// Local calendar date as YYYY-MM-DD (unlike toISOString, this respects the device timezone)
const localDateStr=(d=new Date())=>{
  const y=d.getFullYear();
  const m=String(d.getMonth()+1).padStart(2,"0");
  const day=String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
};

const timeOfDay=()=>{
  const h=new Date().getHours();
  if(h<12) return "Morning";
  if(h<17) return "Afternoon";
  return "Evening";
};

const timeAgo=(isoString)=>{
  if(!isoString) return "";
  const mins=Math.floor((Date.now()-new Date(isoString).getTime())/60000);
  if(mins<2) return "Just now";
  if(mins<60) return `${mins} min ago`;
  if(mins<1440) return `${Math.floor(mins/60)} hr ago`;
  const days=Math.floor(mins/1440);
  return days===1?"Yesterday":`${days} days ago`;
};

// Forces a re-render every `ms` so relative timestamps (timeAgo) stay current
const useTick=(ms)=>{
  const [,setTick]=useState(0);
  useEffect(()=>{
    const id=setInterval(()=>setTick(t=>t+1),ms);
    return()=>clearInterval(id);
  },[ms]);
};

const YELLOW_SCALE=["#FFF6D2","#FFEA9E","#FFD030","#E8AC00","#C98A00"];
const MONO_AVATAR_SCALE=["#FFF59D","#FFF176","#FFEE58","#FDD835","#FBC02D","#F9A825","#F57F17"];
const hashId=seed=>{const s=String(seed??"");let h=0;for(let i=0;i<s.length;i++)h=(h*31+s.charCodeAt(i))>>>0;return h;};
const colorFor=id=>{const h=(hashId(id)*137)%360;return `hsl(${h},62%,48%)`;};
const monoColorFor=id=>MONO_AVATAR_SCALE[hashId(id)%MONO_AVATAR_SCALE.length];

const PENDING_INVITE_KEY="troop_pending_invite";

const ANOMALY_COPY={
  sudden_drop:{title:"We noticed a shift",body:"That's a noticeable change from how you've been feeling lately. Taking a moment to write it down can help."},
  sustained_low:{title:"Checking in on you",body:"Things have felt heavy for a little while. There's no pressure — just a quiet space to put thoughts into words."},
  volatile:{title:"Quite a week",body:"Your mood has shifted a lot recently. Writing down what's going on can bring some clarity."},
  low_mood:{title:"Why do you think you feel this way?",body:"A couple of sentences can help you spot what's going on for you over time."},
  great_mood:{title:"That's awesome!",body:"What's making your day so great? Jotting it down is a nice way to look back on later.",
    placeholder:"What's making today so great?"},
};

// Personal mood-based prompts — unlike anomaly reflections, these never imply the troop was notified
const PERSONAL_PROMPT_TYPES=["low_mood","great_mood"];

// Converts API checkin array → 7-element week array (null = no check-in)
function buildWeekHistory(checkins) {
  return buildHistory(checkins, 7);
}

function buildHistory(checkins, days) {
  const result = Array(days).fill(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  checkins.forEach(c => {
    const date = new Date(c.checked_in_on + "T00:00:00");
    const daysAgo = Math.round((today - date) / 86400000);
    const idx = days - 1 - daysAgo;
    if (idx >= 0 && idx < days) result[idx] = c.mood;
  });
  return result;
}

// Counts consecutive checked-in days ending today (or yesterday, if today's not in yet)
function computeStreak(checkins) {
  const days = new Set(checkins.map(c => c.checked_in_on));
  const cursor = new Date();
  if (!days.has(localDateStr(cursor))) cursor.setDate(cursor.getDate() - 1);

  let streak = 0;
  while (days.has(localDateStr(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

// ── Atoms ──────────────────────────────────────────────────────────────────────
function Logo(){
  return(
    <img src={require('./troop-logo1.png')} height="65" alt="troop"
      style={{mixBlendMode:"screen"}}/>
  );
}
function Avi({i,bc="#FFB870",sz=40,dark=false}){
  return(
    <div style={{width:sz,height:sz,borderRadius:"50%",flexShrink:0,background:bc,
      display:"flex",alignItems:"center",justifyContent:"center",
      fontWeight:700,fontSize:sz*.36,color:dark?"rgba(0,0,0,.6)":"rgba(255,255,255,.9)",letterSpacing:0}}>{i}</div>
  );
}
function Toggle({on,onToggle}){
  return(
    <div onClick={onToggle} style={{width:46,height:26,borderRadius:13,cursor:"pointer",flexShrink:0,
      background:on?C.orange:"#D0C0B0",position:"relative",transition:"background .2s"}}>
      <div style={{position:"absolute",top:3,left:on?23:3,width:20,height:20,borderRadius:"50%",
        background:C.white,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.25)"}}/>
    </div>
  );
}
function Chip({label,color=C.orange,bg="rgba(207,64,8,.1)"}){
  return(
    <div style={{display:"inline-flex",alignItems:"center",padding:"3px 9px",borderRadius:20,
      background:bg,color,fontSize:10,fontWeight:700,letterSpacing:.4,textTransform:"uppercase"}}>
      {label}
    </div>
  );
}

// ── Auth & Loading ─────────────────────────────────────────────────────────────
function LoadingScreen(){
  return(
    <div style={{maxWidth:400,margin:"0 auto",height:"100dvh",display:"flex",
      flexDirection:"column",background:C.orange,fontFamily:"'Inter',system-ui,sans-serif",
      alignItems:"center",justifyContent:"center"}}>
      <Logo/>
    </div>
  );
}

function AuthScreen(){
  const [busy,setBusy]=useState(false);
  const [email,setEmail]=useState("");
  const [magicStatus,setMagicStatus]=useState(null);
  // Pass the invite code through the auth redirect so it survives when the
  // magic link or OAuth callback opens in a different browser (e.g. system browser
  // vs Messenger's in-app browser).
  // Read from the URL first (effect hasn't run yet on first render) then fall
  // back to localStorage (subsequent renders after replaceState cleans the URL).
  const pendingInvite=
    new URLSearchParams(window.location.search).get("invite")||
    localStorage.getItem(PENDING_INVITE_KEY);
  const redirectBase=pendingInvite
    ?`${window.location.origin}/?invite=${pendingInvite}`
    :window.location.origin;
  const signIn=async(provider)=>{
    setBusy(true);
    await supabase.auth.signInWithOAuth({
      provider,
      options:{redirectTo:redirectBase},
    });
  };
  const sendMagicLink=async()=>{
    if(!email.trim()) return;
    setMagicStatus("sending");
    const {error}=await supabase.auth.signInWithOtp({
      email:email.trim(),
      options:{emailRedirectTo:redirectBase},
    });
    setMagicStatus(error?{error:error.message}:"sent");
  };
  return(
    <div style={{maxWidth:400,margin:"0 auto",height:"100dvh",display:"flex",
      flexDirection:"column",background:C.orange,fontFamily:"'Inter',system-ui,sans-serif",
      alignItems:"center",justifyContent:"center",padding:"0 32px",boxSizing:"border-box",
      boxShadow:"0 0 60px rgba(0,0,0,.15)"}}>
      <Logo/>
      <div style={{color:C.white,fontSize:22,fontWeight:700,marginTop:24,marginBottom:8,
        letterSpacing:"-.3px"}}>Welcome to Troop</div>
      <div style={{color:"rgba(255,255,255,.7)",fontSize:14,textAlign:"center",
        marginBottom:48,lineHeight:1.7,fontWeight:500}}>
        A quiet space to track how you're feeling and look out for the people you care about.
      </div>
      <button onClick={()=>signIn("google")} disabled={busy} style={{
        width:"100%",padding:"14px 0",borderRadius:12,border:"none",
        background:C.white,color:C.txtDk,fontWeight:700,fontSize:15,
        cursor:"pointer",fontFamily:"inherit",marginBottom:12,letterSpacing:.2}}>
        Continue with Google
      </button>
      <button onClick={()=>signIn("apple")} disabled={busy} style={{
        width:"100%",padding:"14px 0",borderRadius:12,
        border:"2px solid rgba(255,255,255,.35)",background:"transparent",
        color:C.white,fontWeight:700,fontSize:15,
        cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
        Continue with Apple
      </button>

      <div style={{display:"flex",alignItems:"center",gap:10,width:"100%",margin:"20px 0"}}>
        <div style={{flex:1,height:1,background:"rgba(255,255,255,.2)"}}/>
        <div style={{color:"rgba(255,255,255,.5)",fontSize:11,fontWeight:700,letterSpacing:.5}}>OR</div>
        <div style={{flex:1,height:1,background:"rgba(255,255,255,.2)"}}/>
      </div>

      {magicStatus==="sent"?(
        <div style={{color:C.white,fontSize:13,textAlign:"center",fontWeight:600,lineHeight:1.6}}>
          Check {email} for a sign-in link.
        </div>
      ):(
        <>
          <input className="troop-input" type="email" value={email} placeholder="you@example.com"
            onChange={e=>{setEmail(e.target.value);setMagicStatus(null);}}
            onKeyDown={e=>{if(e.key==="Enter") sendMagicLink();}}
            style={{background:"rgba(255,255,255,.12)",border:"none",outline:"none",
              width:"100%",fontSize:14,fontWeight:500,fontFamily:"inherit",
              padding:"13px 14px",borderRadius:12,marginBottom:10,boxSizing:"border-box"}}/>
          {magicStatus?.error&&
            <div style={{color:"#FFD3D3",fontSize:12,fontWeight:600,marginBottom:10,textAlign:"center"}}>
              {magicStatus.error}
            </div>}
          <button onClick={sendMagicLink} disabled={busy||magicStatus==="sending"} style={{
            width:"100%",padding:"14px 0",borderRadius:12,border:"none",
            background:"rgba(255,255,255,.15)",color:C.white,fontWeight:700,fontSize:15,
            cursor:"pointer",fontFamily:"inherit",letterSpacing:.2,
            opacity:magicStatus==="sending"?.6:1}}>
            {magicStatus==="sending"?"Sending…":"Continue with email"}
          </button>
        </>
      )}

      <div style={{color:"rgba(255,255,255,.45)",fontSize:11,marginTop:32,
        textAlign:"center",lineHeight:1.7,fontWeight:500}}>
        Your mood data is private and never shared with friends.
      </div>
    </div>
  );
}

// ── Cap Error Modal ─────────────────────────────────────────────────────────────
function CapErrorModal({message,onClose}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        <div style={{fontWeight:700,fontSize:18,color:C.txtDk,marginBottom:12,letterSpacing:"-.2px"}}>
          You've reached the limit
        </div>
        <div style={{fontSize:14,color:C.txtMd,lineHeight:1.7,marginBottom:24}}>
          {message}
        </div>
        <button onClick={onClose} style={{width:"100%",padding:14,borderRadius:13,border:"none",
          background:C.orange,color:C.white,fontWeight:700,fontSize:15,cursor:"pointer",
          fontFamily:"inherit"}}>
          Got it
        </button>
      </div>
    </div>
  );
}

// ── Reflection Modal ────────────────────────────────────────────────────────────
function ReflectionModal({anomaly,onSave,onSkip}){
  const [text,setText]=useState("");
  const copy=ANOMALY_COPY[anomaly.type]||ANOMALY_COPY.sudden_drop;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{width:40,height:40,borderRadius:12,background:C.amberBg,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>✦</div>
          <div>
            <div style={{fontWeight:700,fontSize:16,color:C.txtDk,lineHeight:1.2}}>{copy.title}</div>
            <div style={{fontSize:11,color:C.txtMd,marginTop:1}}>Private reflection</div>
          </div>
        </div>
        <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:18}}>
          {copy.body}
          <span style={{display:"block",marginTop:8,fontWeight:600,color:C.orange}}>
            This note is completely private — only you will ever see it.
          </span>
        </div>
        <textarea className="troop-reflect" value={text} onChange={e=>setText(e.target.value)}
          placeholder={copy.placeholder||"What's going on for you today? No pressure to explain everything."}
          style={{width:"100%",minHeight:90,padding:"12px 14px",
            border:`1.5px solid ${C.creamDk}`,borderRadius:12,
            background:C.cream,fontSize:13,color:C.txtDk,fontFamily:"inherit",
            resize:"none",outline:"none",boxSizing:"border-box",lineHeight:1.65}}/>
        <button onClick={()=>onSave(text)} style={{marginTop:12,width:"100%",padding:13,
          borderRadius:12,border:"none",background:C.orange,color:C.white,
          fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
          Save note
        </button>
        <button onClick={onSkip} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
          border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
          fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Not right now
        </button>
      </div>
    </div>
  );
}

// ── Alert Confirm Modal ─────────────────────────────────────────────────────────
const ROUGH_ALERT_TYPES=new Set(['rough_first','rough_repeat','rough_sustained']);

function AlertConfirmModal({anomaly,onConfirm,onDecline,onHelp}){
  const isRough=ROUGH_ALERT_TYPES.has(anomaly.type);
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{width:40,height:40,borderRadius:12,background:C.amberBg,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>⟡</div>
          <div>
            <div style={{fontWeight:700,fontSize:16,color:C.txtDk,lineHeight:1.2}}>Let your troop know?</div>
            <div style={{fontSize:11,color:C.txtMd,marginTop:1}}>Everyone has tough days — that's what your troop is for</div>
          </div>
        </div>
        <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:14}}>
          {isRough
            ?"Everyone goes through it. Letting the people who care about you know you're having a tough one isn't weak — it's exactly what your troop is there for. They won't see your mood score or anything you wrote."
            :"Ups and downs are part of it. The people in your troop who watch out for you can be quietly notified — they won't see your mood or notes, just that a check-in from them could help."
          }
        </div>
        <div style={{background:C.cream,borderRadius:12,padding:"12px 14px",marginBottom:16,
          border:`1px solid ${C.creamDk}`}}>
          <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,
            color:C.txtMd,marginBottom:6}}>What they'll receive</div>
          <div style={{fontSize:13,color:C.txtDk,lineHeight:1.6,fontStyle:"italic"}}>
            {isRough
              ?'"[Your name] is having a tough day — a quick message could make a real difference."'
              :'"[Your name]\'s pattern has shifted lately — a quiet check-in could help."'
            }
          </div>
          <div style={{fontSize:11,color:C.txtMd,marginTop:8,lineHeight:1.5}}>
            They can choose to reach out or not — there's no obligation on anyone.
          </div>
        </div>
        <button onClick={onConfirm} style={{width:"100%",padding:13,borderRadius:12,border:"none",
          background:C.orange,color:C.white,fontWeight:700,fontSize:14,
          cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
          Let my troop know
        </button>
        <button onClick={onDecline} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
          border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
          fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Keep this private
        </button>
        <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.creamDk}`}}>
          <div style={{fontSize:12,color:C.txtMd,lineHeight:1.6,marginBottom:10,textAlign:"center"}}>
            Whatever you choose, free and confidential support is always there if you need it.
          </div>
          <button onClick={onHelp} style={{width:"100%",padding:11,borderRadius:12,
            border:`1.5px solid ${C.creamDk}`,background:C.cream,color:C.orange,fontWeight:700,
            fontSize:13,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
            See help options
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Breathing Modal ─────────────────────────────────────────────────────────────
const PHASES=["Breathe in","Hold","Breathe out","Hold"];
const TOTAL_ROUNDS=4;
const PHASE_SECS=4;

function BreathingModal({onClose}){
  const [started,setStarted]=useState(false);
  const [phase,setPhase]=useState(0);
  const [secs,setSecs]=useState(PHASE_SECS);
  const [round,setRound]=useState(1);
  const [done,setDone]=useState(false);

  useEffect(()=>{
    if(!started||done) return;
    const id=setInterval(()=>{
      setSecs(s=>{
        if(s>1) return s-1;
        setPhase(p=>{
          const next=(p+1)%4;
          if(next===0){
            setRound(r=>{
              if(r>=TOTAL_ROUNDS){ setDone(true); return r; }
              return r+1;
            });
          }
          return next;
        });
        return PHASE_SECS;
      });
    },1000);
    return()=>clearInterval(id);
  },[started,done]);

  const isExpanded=phase===0||phase===1;
  const circleColor=phase===0?"#CF4008":phase===2?"#2D7A4F":"rgba(207,64,8,.5)";

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:300}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"28px 24px 48px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.2)",textAlign:"center"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 28px"}}/>

        {!started?(
          <>
            <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(207,64,8,.1)",
              margin:"0 auto 20px",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{width:28,height:28,borderRadius:"50%",background:C.orange}}/>
            </div>
            <div style={{fontWeight:700,fontSize:18,color:C.txtDk,marginBottom:10,letterSpacing:"-.2px"}}>
              Take a breath
            </div>
            <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:6}}>
              Box breathing is a simple way to slow your nervous system down.
            </div>
            <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:28}}>
              It only takes 60 seconds.
            </div>
            <button onClick={()=>setStarted(true)} style={{width:"100%",padding:13,borderRadius:12,
              border:"none",background:C.orange,color:C.white,
              fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
              Begin
            </button>
            <button onClick={onClose} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
              border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
              fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              Maybe another time
            </button>
          </>
        ):done?(
          <>
            <div style={{width:72,height:72,borderRadius:"50%",background:C.greenBg,
              margin:"0 auto 20px",display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:32}}>✓</div>
            <div style={{fontWeight:700,fontSize:18,color:C.txtDk,marginBottom:10,letterSpacing:"-.2px"}}>
              Well done
            </div>
            <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:28}}>
              Take a moment before moving on. Notice how you feel now compared to when you started.
            </div>
            <button onClick={onClose} style={{width:"100%",padding:13,borderRadius:12,
              border:"none",background:C.orange,color:C.white,
              fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
              Done
            </button>
          </>
        ):(
          <>
            <div style={{fontSize:11,fontWeight:700,color:C.txtLt,letterSpacing:.8,
              textTransform:"uppercase",marginBottom:28}}>
              Round {round} of {TOTAL_ROUNDS}
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",
              height:180,marginBottom:28}}>
              <div style={{
                width:isExpanded?160:100,height:isExpanded?160:100,
                borderRadius:"50%",background:circleColor,opacity:.85,
                transition:`width ${PHASE_SECS-0.3}s ease-in-out, height ${PHASE_SECS-0.3}s ease-in-out, background 0.5s`,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <div style={{color:C.white,fontWeight:700,fontSize:28,lineHeight:1}}>{secs}</div>
              </div>
            </div>
            <div style={{fontWeight:700,fontSize:20,color:C.txtDk,letterSpacing:"-.2px",marginBottom:6}}>
              {PHASES[phase]}
            </div>
            <div style={{fontSize:12,color:C.txtLt,fontWeight:500,marginBottom:28}}>
              {phase===0?"Slow and steady through the nose":
               phase===1?"Hold comfortably":
               phase===2?"Gently out through the mouth":
               "Pause before the next breath"}
            </div>
            <button onClick={onClose} style={{width:"100%",padding:11,borderRadius:12,
              border:"none",background:"transparent",color:C.txtLt,fontWeight:600,
              fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              End session
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Celebration Modal ────────────────────────────────────────────────────────────
function pickSuggestion(pinned,members){
  // Prefer pinned friends with an anomaly, then pinned without, then any member
  const withAnomaly=pinned.filter(f=>f.anomaly_type);
  if(withAnomaly.length) return withAnomaly[Math.floor(Math.random()*withAnomaly.length)];
  if(pinned.length) return pinned[Math.floor(Math.random()*pinned.length)];
  if(members.length) return members[Math.floor(Math.random()*members.length)];
  return null;
}

function CelebrationModal({pinned,members,monoColors,onReach,onClose}){
  const suggestion=pickSuggestion(pinned,members);
  const firstName=suggestion?.display_name?.split(" ")[0];
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"28px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>

        <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(207,64,8,.1)",
          display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:C.orange}}/>
        </div>

        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:20,color:C.txtDk,letterSpacing:"-.3px",marginBottom:8}}>
            Great days are worth sharing
          </div>
          <div style={{fontSize:14,color:C.txtMd,fontWeight:500,lineHeight:1.7,maxWidth:"32ch",margin:"0 auto"}}>
            {suggestion
              ? `You're having a good one. ${firstName} might be finding things a bit tougher right now — a message could mean a lot.`
              : "You're having a good one. Reaching out to someone while you're feeling this way could mean more than you know."}
          </div>
        </div>

        {suggestion&&(
          <div style={{background:C.cream,borderRadius:14,padding:"12px 16px",
            display:"flex",alignItems:"center",gap:12,marginBottom:16,border:`1px solid ${C.creamDk}`}}>
            <Avi i={suggestion.avatar_initial} bc={monoColors?monoColorFor(suggestion.id):colorFor(suggestion.id)} dark={monoColors} sz={40}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>{suggestion.display_name}</div>
              {suggestion.anomaly_type&&(
                <div style={{fontSize:11,color:C.txtLt,fontWeight:600,marginTop:2}}>
                  {suggestion.anomaly_type==="sudden_drop"?"Having a tough time lately":"Feeling low recently"}
                </div>
              )}
            </div>
          </div>
        )}

        {suggestion&&(
          <button onClick={()=>{onReach(suggestion);onClose();}}
            style={{width:"100%",padding:13,borderRadius:12,border:"none",
              background:C.orange,color:C.white,fontWeight:700,fontSize:14,
              cursor:"pointer",fontFamily:"inherit",letterSpacing:.2,marginBottom:8}}>
            Reach out to {firstName}
          </button>
        )}
        <button onClick={onClose}
          style={{width:"100%",padding:11,borderRadius:12,border:"none",
            background:"transparent",color:C.txtLt,fontWeight:500,
            fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Maybe another time
        </button>
      </div>
    </div>
  );
}

// ── Reset Data Modal ────────────────────────────────────────────────────────────
function ResetDataModal({onConfirm,onClose}){
  const [busy,setBusy]=useState(false);
  const confirm=async()=>{
    if(busy) return;
    setBusy(true);
    await onConfirm();
    onClose();
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        <div style={{fontWeight:700,fontSize:18,color:C.txtDk,marginBottom:8,letterSpacing:"-.2px"}}>
          Sometimes we all need a fresh slate
        </div>
        <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:20}}>
          This will permanently delete the following data from your account:
        </div>
        {[
          "All check-in history and mood data",
          "All personal notes and reflections",
          "Your good moments",
        ].map(item=>(
          <div key={item} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:C.orange,flexShrink:0}}/>
            <div style={{fontSize:13,color:C.txtDk,fontWeight:500}}>{item}</div>
          </div>
        ))}
        <div style={{marginTop:16,padding:"12px 14px",borderRadius:12,background:C.greenBg,
          border:`1px solid rgba(45,122,79,.15)`,fontSize:12,color:C.green,fontWeight:500,lineHeight:1.6}}>
          Your troop connections, pins, and settings will not be affected.
        </div>
        <button onClick={confirm} disabled={busy}
          style={{marginTop:20,width:"100%",padding:13,borderRadius:12,border:"none",
            background:"#C62828",color:C.white,fontWeight:700,fontSize:14,
            cursor:"pointer",fontFamily:"inherit",letterSpacing:.2,opacity:busy?.6:1}}>
          {busy?"Resetting…":"Yes, reset my data"}
        </button>
        <button onClick={onClose} disabled={busy}
          style={{marginTop:8,width:"100%",padding:11,borderRadius:12,border:"none",
            background:"transparent",color:C.txtMd,fontWeight:600,
            fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Reach Modal ─────────────────────────────────────────────────────────────────
const REACH_SUGGESTION_PAIRS=[
  [
    "You've been on my mind lately. How are you going?",
    "Just wanted to check in. It's fine if you're in a rough patch — we all have them.",
  ],
  [
    "Want to grab a coffee or go for a walk this week?",
    "Hey, how are you going? It'd be good to catch up.",
  ],
  [
    "Thinking of you — want to catch up this week?",
    "Just dropping in to say hey. How are things?",
  ],
];

function ReachModal({friend,monoColors,onSend,onClose}){
  const [msg,setMsg]=useState("");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState(null);
  const [pairIdx,setPairIdx]=useState(0);
  const [autoRotate,setAutoRotate]=useState(true);
  const firstName=friend.display_name.split(" ")[0];
  const currentPair=REACH_SUGGESTION_PAIRS[pairIdx%REACH_SUGGESTION_PAIRS.length];

  useEffect(()=>{
    if(!autoRotate) return;
    const id=setInterval(()=>setPairIdx(i=>(i+1)%REACH_SUGGESTION_PAIRS.length),10000);
    return()=>clearInterval(id);
  },[autoRotate]);

  const selectSuggestion=(s)=>{ setMsg(s); setAutoRotate(false); };
  const nextPair=()=>{ setPairIdx(i=>(i+1)%REACH_SUGGESTION_PAIRS.length); setAutoRotate(false); };

  const send=async(withMsg)=>{
    if(busy) return;
    setBusy(true); setErr(null);
    try{
      await onSend(friend.id, withMsg&&msg.trim()?msg.trim():undefined);
      onClose();
    }catch(e){ setErr(e.message); setBusy(false); }
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 36px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 20px"}}/>

        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
          <Avi i={friend.avatar_initial} bc={monoColors?monoColorFor(friend.id):colorFor(friend.id)} dark={monoColors} sz={44}/>
          <div>
            <div style={{fontWeight:700,fontSize:16,color:C.txtDk,letterSpacing:"-.2px"}}>
              Check in with {firstName}
            </div>
            <div style={{fontSize:12,color:C.txtMd,marginTop:2,fontWeight:500}}>
              A personal message lands better than silence
            </div>
          </div>
        </div>

        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",
          letterSpacing:.6,marginBottom:6}}>Write something</div>
        <textarea className="troop-reflect" value={msg} onChange={e=>{setMsg(e.target.value);setAutoRotate(false);}}
          placeholder="What would you say to them right now?"
          style={{width:"100%",minHeight:88,padding:"12px 14px",
            border:`1.5px solid ${msg.trim()?C.orange:C.creamDk}`,borderRadius:12,
            background:C.cream,fontSize:13,color:C.txtDk,fontFamily:"inherit",
            resize:"none",outline:"none",boxSizing:"border-box",lineHeight:1.65,
            transition:"border-color .15s"}}/>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          margin:"14px 0 8px"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.6}}>
            Or try one of these
          </div>
          <button onClick={nextPair} style={{background:"none",border:"none",cursor:"pointer",
            fontSize:11,fontWeight:600,color:C.orange,fontFamily:"inherit",padding:0,letterSpacing:.2}}>
            Give me some others
          </button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {currentPair.map((s,i)=>(
            <button key={i} onClick={()=>selectSuggestion(s)}
              style={{textAlign:"left",padding:"9px 12px",borderRadius:10,cursor:"pointer",
                fontFamily:"inherit",fontSize:13,lineHeight:1.55,fontWeight:500,
                background:msg===s?C.orange+"18":"rgba(0,0,0,.04)",
                color:msg===s?C.orange:C.txtMd,
                border:`1.5px solid ${msg===s?C.orange+"44":C.creamDk}`,
                transition:"all .12s"}}>
              {s}
            </button>
          ))}
        </div>

        {err&&<div style={{fontSize:12,color:"#C0392B",fontWeight:500,marginTop:10,textAlign:"center",lineHeight:1.5}}>{err}</div>}

        <button onClick={()=>send(true)} disabled={busy||!msg.trim()} style={{marginTop:14,width:"100%",padding:13,
          borderRadius:12,border:"none",background:msg.trim()?C.orange:"rgba(0,0,0,.08)",
          color:msg.trim()?C.white:C.txtLt,
          fontWeight:700,fontSize:14,cursor:msg.trim()&&!busy?"pointer":"default",
          fontFamily:"inherit",letterSpacing:.2}}>
          Send message
        </button>
        <button onClick={()=>send(false)} disabled={busy} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
          border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
          fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Send without message
        </button>
        <button onClick={onClose} disabled={busy} style={{marginTop:4,width:"100%",padding:9,borderRadius:12,
          border:"none",background:"transparent",color:C.txtLt,fontWeight:500,
          fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Home ────────────────────────────────────────────────────────────────────────
function ProfileModal({profile,email,onClose,onSettings}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,marginBottom:22}}>
          <Avi i={profile?.avatar_initial??"?"} bc={C.yellow} sz={64} dark/>
          <div style={{fontWeight:700,fontSize:18,color:C.txtDk,letterSpacing:"-.2px"}}>{profile?.display_name}</div>
          {email&&<div style={{fontSize:12,color:C.txtMd,fontWeight:500}}>{email}</div>}
        </div>
        <button onClick={onSettings} style={{width:"100%",padding:13,
          borderRadius:12,border:"none",background:C.orange,color:C.white,
          fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
          Go to Settings
        </button>
        <button onClick={onClose} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
          border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
          fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Close
        </button>
      </div>
    </div>
  );
}
function InboxCard({reach,onAck,monoColors}){
  const [replying,setReplying]=useState(false);
  const [text,setText]=useState("");
  const [busy,setBusy]=useState(false);
  const submit=async(reply)=>{
    setBusy(true);
    await onAck(reach.id,reply||null);
  };
  return(
    <div style={{background:C.white,borderRadius:14,padding:"12px 14px",
      border:`1px solid ${C.creamDk}`,boxShadow:"0 1px 6px rgba(0,0,0,.05)",marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:reach.message||replying?10:0}}>
        <Avi i={reach.sender?.avatar_initial} bc={monoColors?monoColorFor(reach.sender?.id):colorFor(reach.sender?.id)} sz={36}/>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>{reach.sender?.display_name}</div>
          <div style={{fontSize:11,color:C.orange,fontWeight:600,marginTop:1}}>Checked in on you · {timeAgo(reach.created_at)}</div>
        </div>
      </div>
      {reach.message&&(
        <div style={{fontSize:13,color:C.txtMd,lineHeight:1.6,marginBottom:10,
          background:C.cream,borderRadius:8,padding:"8px 10px",
          border:`1px solid ${C.creamDk}`,fontStyle:"italic"}}>
          "{reach.message}"
        </div>
      )}
      {replying?(
        <>
          <textarea value={text} onChange={e=>setText(e.target.value)}
            placeholder="Write a reply…"
            style={{width:"100%",minHeight:70,padding:"10px 12px",
              border:`1.5px solid ${C.creamDk}`,borderRadius:10,
              background:C.cream,fontSize:13,color:C.txtDk,fontFamily:"inherit",
              resize:"none",outline:"none",boxSizing:"border-box",lineHeight:1.6}}/>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button onClick={()=>submit(text.trim()||null)} disabled={busy} style={{
              flex:1,padding:"9px 0",borderRadius:10,border:"none",
              background:C.orange,color:C.white,fontWeight:700,fontSize:13,
              cursor:"pointer",fontFamily:"inherit",opacity:busy?.6:1}}>
              {busy?"Sending…":"Send reply"}
            </button>
            <button onClick={()=>setReplying(false)} disabled={busy} style={{
              padding:"9px 14px",borderRadius:10,border:`1px solid ${C.creamDk}`,
              background:"transparent",color:C.txtMd,fontWeight:600,fontSize:12,
              cursor:"pointer",fontFamily:"inherit"}}>
              Cancel
            </button>
          </div>
        </>
      ):(
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>submit(null)} disabled={busy} style={{
            flex:1,padding:"8px 0",borderRadius:10,border:`1px solid ${C.creamDk}`,
            background:C.cream,color:C.txtMd,fontWeight:600,fontSize:12,
            cursor:"pointer",fontFamily:"inherit",opacity:busy?.6:1}}>
            {busy?"…":"Got it"}
          </button>
          <button onClick={()=>setReplying(true)} disabled={busy} style={{
            flex:1,padding:"8px 0",borderRadius:10,border:"none",
            background:C.orange,color:C.white,fontWeight:700,fontSize:12,
            cursor:"pointer",fontFamily:"inherit"}}>
            Reply
          </button>
        </div>
      )}
    </div>
  );
}

function HomeScreen({mood,setMood,note,setNote,checkedIn,submittedMood,doCheckin,doUpdateCheckin,editingCheckin,setEditingCheckin,hasUpdatedToday,pinned,reached,openReach,alertSent,profile,email,setTab,showEmojis,monoColors,streak,reachInbox,onAckReach}){
  useTick(60000);
  const [showProfile,setShowProfile]=useState(false);
  const alertFriends=pinned.filter(f=>f.anomaly_type);
  const firstName=profile?.display_name?.split(" ")[0]??"";
  return(
    <div>
      <div style={{background:C.orange,padding:"18px 20px 0"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <Logo/>
          <button onClick={()=>setShowProfile(true)} style={{background:"none",border:"none",
            padding:0,cursor:"pointer",borderRadius:"50%",lineHeight:0}}>
            <Avi i={profile?.avatar_initial??"?"} bc={C.yellow} sz={38} dark/>
          </button>
        </div>
        <div style={{color:"rgba(255,255,255,.6)",fontSize:12,fontWeight:500,letterSpacing:.2}}>Good {timeOfDay().toLowerCase()}</div>
        <div style={{color:C.white,fontSize:22,fontWeight:700,marginBottom:18,letterSpacing:"-.3px"}}>{firstName}</div>

        <div style={{background:C.orangeDk,borderRadius:"16px 16px 0 0",padding:"20px 18px 22px"}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",
            color:"rgba(255,255,255,.5)",marginBottom:14}}>Daily Check-In</div>

          {!checkedIn?(
            <>
              <div style={{color:C.yellow,fontWeight:700,fontSize:15,marginBottom:16,letterSpacing:"-.2px"}}>
                How are you feeling today?
              </div>
              <div style={{display:"flex",gap:6,marginBottom:14}}>
                {MOODS.map(m=>(
                  <div key={m.v} onClick={()=>setMood(m.v)} style={{
                    flex:1,padding:"10px 4px 8px",borderRadius:12,cursor:"pointer",
                    display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                    background:mood===m.v?"rgba(255,255,255,.95)":"rgba(255,255,255,.14)",
                    border:`1.5px solid ${mood===m.v?C.yellow:"transparent"}`,transition:"all .12s"}}>
                    {showEmojis&&<span style={{fontSize:20}}>{m.e}</span>}
                    <span style={{fontSize:showEmojis?9:11,fontWeight:700,letterSpacing:.3,
                      color:mood===m.v?C.orange:"rgba(255,255,255,.6)"}}>{m.lbl.toUpperCase()}</span>
                  </div>
                ))}
              </div>
              <input className="troop-input" value={note} onChange={e=>setNote(e.target.value)}
                placeholder="Add a note (optional)"
                style={{background:"rgba(255,255,255,.12)",border:"none",outline:"none",
                  width:"100%",fontSize:13,fontWeight:500,fontFamily:"inherit",
                  padding:"11px 14px",borderRadius:10,marginBottom:12,boxSizing:"border-box"}}/>
              <button onClick={doCheckin} disabled={!mood} style={{width:"100%",padding:13,borderRadius:12,
                border:"none",background:mood?C.yellow:"rgba(255,255,255,.15)",
                color:mood?C.txtDk:"rgba(255,255,255,.3)",
                fontWeight:700,fontSize:13,cursor:mood?"pointer":"default",fontFamily:"inherit",
                letterSpacing:.3,transition:"all .12s"}}>
                Log today
              </button>
            </>
          ):editingCheckin?(
            <>
              <div style={{color:C.yellow,fontWeight:700,fontSize:15,marginBottom:16,letterSpacing:"-.2px"}}>
                How are you feeling now?
              </div>
              <div style={{display:"flex",gap:6,marginBottom:14}}>
                {MOODS.map(m=>(
                  <div key={m.v} onClick={()=>setMood(m.v)} style={{
                    flex:1,padding:"10px 4px 8px",borderRadius:12,cursor:"pointer",
                    display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                    background:mood===m.v?"rgba(255,255,255,.95)":"rgba(255,255,255,.14)",
                    border:`1.5px solid ${mood===m.v?C.yellow:"transparent"}`,transition:"all .12s"}}>
                    {showEmojis&&<span style={{fontSize:20}}>{m.e}</span>}
                    <span style={{fontSize:showEmojis?9:11,fontWeight:700,letterSpacing:.3,
                      color:mood===m.v?C.orange:"rgba(255,255,255,.6)"}}>{m.lbl.toUpperCase()}</span>
                  </div>
                ))}
              </div>
              <input className="troop-input" value={note} onChange={e=>setNote(e.target.value)}
                placeholder="Add a note (optional)"
                style={{background:"rgba(255,255,255,.12)",border:"none",outline:"none",
                  width:"100%",fontSize:13,fontWeight:500,fontFamily:"inherit",
                  padding:"11px 14px",borderRadius:10,marginBottom:12,boxSizing:"border-box"}}/>
              <button onClick={doUpdateCheckin} disabled={!mood} style={{width:"100%",padding:13,borderRadius:12,
                border:"none",background:mood?C.yellow:"rgba(255,255,255,.15)",
                color:mood?C.txtDk:"rgba(255,255,255,.3)",
                fontWeight:700,fontSize:13,cursor:mood?"pointer":"default",fontFamily:"inherit",
                letterSpacing:.3,transition:"all .12s"}}>
                Update
              </button>
              <button onClick={()=>setEditingCheckin(false)} style={{marginTop:8,width:"100%",padding:10,
                borderRadius:12,border:"none",background:"transparent",color:"rgba(255,255,255,.45)",
                fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                Cancel
              </button>
            </>
          ):(
            <div style={{textAlign:"center",padding:"14px 0 10px"}}>
              {showEmojis
                ?<div style={{fontSize:44}}>{me(submittedMood)}</div>
                :<div style={{fontSize:24,fontWeight:700,color:mc(submittedMood,monoColors)}}>{ml(submittedMood)}</div>}
              <div style={{color:C.yellow,fontWeight:700,fontSize:16,marginTop:10,letterSpacing:"-.2px"}}>
                Logged
              </div>
              <div style={{color:"rgba(255,255,255,.55)",fontSize:12,marginTop:4,fontWeight:500}}>
                {alertSent?"Your troop has been quietly notified":"See you tomorrow"}
              </div>
              {!hasUpdatedToday&&(
                <button onClick={()=>{setMood(submittedMood);setEditingCheckin(true);}}
                  style={{marginTop:12,padding:"7px 18px",borderRadius:20,border:"1.5px solid rgba(255,255,255,.25)",
                    background:"transparent",color:"rgba(255,255,255,.6)",fontSize:11,fontWeight:600,
                    cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
                  Update mood
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{background:C.cream,padding:"18px 16px 32px"}}>
        {reachInbox?.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",
              color:C.txtMd,marginBottom:10}}>They reached out</div>
            {reachInbox.map(r=>(
              <InboxCard key={r.id} reach={r} onAck={onAckReach} monoColors={monoColors}/>
            ))}
          </div>
        )}
        {alertFriends.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",
              color:C.txtMd,marginBottom:10}}>Needs attention</div>
            {alertFriends.map(f=>(
              <div key={f.id} style={{background:C.white,borderRadius:14,padding:"12px 14px",
                display:"flex",alignItems:"center",gap:10,
                border:`1px solid ${C.creamDk}`,boxShadow:"0 1px 6px rgba(0,0,0,.05)",marginBottom:6}}>
                <Avi i={f.avatar_initial} bc={monoColors?monoColorFor(f.id):colorFor(f.id)} dark={monoColors} sz={36}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.txtDk,letterSpacing:"-.1px"}}>{f.display_name}</div>
                  <div style={{fontSize:11,color:C.amber,fontWeight:600,marginTop:1}}>{f.anomaly_type==='rough'?'Having a tough day':'Unusual pattern detected'}</div>
                </div>
                <button onClick={()=>openReach(f)} style={{
                  background:reached[f.id]?C.greenBg:C.orange,
                  color:reached[f.id]?C.green:C.white,
                  border:"none",borderRadius:20,padding:"6px 12px",
                  fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit",
                  letterSpacing:.2}}>
                  {reached[f.id]?"Sent":"Check in"}
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",color:C.txtMd}}>Your Troop</div>
          {streak>0&&
            <div style={{background:C.orange,color:C.white,fontSize:10,fontWeight:700,
              padding:"4px 10px",borderRadius:20,letterSpacing:.3}}>{streak} day streak</div>}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {pinned.filter(f=>!f.anomaly_type).map(f=>(
            <div key={f.id} style={{background:C.white,borderRadius:13,padding:"11px 14px",
              display:"flex",alignItems:"center",gap:10,
              border:`1px solid ${C.creamDk}`,boxShadow:"0 1px 5px rgba(0,0,0,.04)"}}>
              <Avi i={f.avatar_initial} bc={monoColors?monoColorFor(f.id):colorFor(f.id)} dark={monoColors} sz={34}/>
              <span style={{flex:1,fontWeight:600,fontSize:14,color:C.txtDk}}>{f.display_name}</span>
              {f.anomaly_type&&<Chip label={f.anomaly_type==='sustained_low'?'Sustained low':'Unusual pattern'} color={C.amber} bg={C.amberBg}/>}
            </div>
          ))}
        </div>
        <div style={{marginTop:14,padding:"10px 14px",borderRadius:10,
          background:"rgba(0,0,0,.035)",border:`1px solid ${C.creamDk}`}}>
          <div style={{fontSize:11,color:C.txtMd,fontWeight:500,lineHeight:1.6}}>
            <strong style={{fontWeight:700}}>Privacy:</strong> Your troop only sees that you checked in — never your mood score or history. Alerts only fire on genuine irregular patterns.
          </div>
        </div>
      </div>
      {showProfile&&
        <ProfileModal profile={profile} email={email} onClose={()=>setShowProfile(false)}
          onSettings={()=>{setTab("settings");setShowProfile(false);}}/>}
    </div>
  );
}

// ── Insights ────────────────────────────────────────────────────────────────────
const JOURNAL_PROMPTS={
  1:[
    "Where in your body do you feel the heaviest right now? Describe the sensation without needing to change it.",
    "Notice your breath — is it shallow, held, or rushed? After a few slow breaths, write what you notice now.",
    "Where is your body holding tension — jaw, shoulders, hands? Just name it, no need to interpret it.",
    "Step outside or look toward a window for a moment. What do you notice in the air, light, or sound?",
  ],
  2:[
    "Where does low energy sit in your body today — chest, limbs, eyes? What does it seem to want (rest, quiet, stillness)?",
    "Have your hunger or thirst cues changed today? What do you notice about that?",
    "Notice your posture right now. If you adjust it slightly, what do you observe?",
    "Is there one small physical comfort available to you right now — warmth, a soft chair, a meal? Where do you notice that in your body?",
  ],
  3:[
    "Scan from head to toe — is there a part of you that feels more at ease than the rest? What's different there?",
    "Is your breath steady, or is there a subtle tension somewhere you hadn't noticed?",
    "Bring to mind one ordinary thing your body did for you today. What do you notice as you acknowledge it?",
    "If you spent a minute outside right now, what would you notice first — temperature, wind, light, smell?",
  ],
  4:[
    "Where do you notice lightness or ease in your body right now? Describe it.",
    "Is there a natural smile or looseness in your face? What's around you when you notice it?",
    "Bring to mind something small you appreciate today. Where do you notice that in your body?",
    "Notice something in nature nearby — a plant, the sky, the weather. Does looking at it change your breath or energy at all?",
  ],
  5:[
    "Where does this feeling show up first in your body — chest, stomach, face, hands?",
    "Is there an urge to move or stretch? Follow it for a moment and notice what happens.",
    "What's one thing right now — a person, place, or moment — you feel appreciation for? Where do you notice that in your body?",
    "If this feeling were a season or moment in nature, what would it be — and what is your body doing in that scene?",
  ],
};

const TIMEFRAMES=[{label:"1W",days:7},{label:"2W",days:14},{label:"1M",days:30}];

function InsightsScreen({myHistory,allCheckins,journal,onAddJournal,showEmojis,monoColors,submittedMood}){
  const [timeframe,setTimeframe]=useState(7);
  const history=allCheckins.length?buildHistory(allCheckins,timeframe):myHistory;
  const valid=history.filter(v=>v!=null);
  const avg=valid.length?(valid.reduce((a,b)=>a+b,0)/valid.length).toFixed(1):"—";
  const bestV=valid.length?Math.max(...valid):null;
  const dayNames=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const days=Array.from({length:timeframe},(_,i)=>{
    const d=new Date();
    d.setDate(d.getDate()-(timeframe-1-i));
    return {letter:dayNames[d.getDay()].slice(0,1),date:d.getDate()};
  });
  const highNotes=journal.filter(j=>j.mood>=4&&j.text);
  const [moment,setMoment]=useState(null);
  const [addingMemory,setAddingMemory]=useState(false);
  const [memoryText,setMemoryText]=useState("");
  const [savingMemory,setSavingMemory]=useState(false);

  // Journaling
  const [promptIdx,setPromptIdx]=useState(()=>Math.floor(Math.random()*4));
  const [promptShown,setPromptShown]=useState(false);
  const [reflectionText,setReflectionText]=useState("");
  const [savingReflection,setSavingReflection]=useState(false);

  const prompts=submittedMood?JOURNAL_PROMPTS[submittedMood]:null;
  const currentPrompt=prompts?prompts[promptIdx%prompts.length]:null;
  const nextPrompt=()=>setPromptIdx(i=>(i+1)%prompts.length);

  const pickMoment=()=>{
    if(!highNotes.length) return;
    const pool=highNotes.length>1?highNotes.filter(j=>j!==moment):highNotes;
    setMoment(pool[Math.floor(Math.random()*pool.length)]);
  };
  const saveMemory=async()=>{
    if(!memoryText.trim()||savingMemory) return;
    setSavingMemory(true);
    try{
      await api.reflections.save(5,'great_mood',memoryText.trim());
      const date=new Date().toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});
      onAddJournal({date,mood:5,text:memoryText.trim()});
      setMemoryText("");
      setAddingMemory(false);
    }catch(err){ console.error(err); }
    setSavingMemory(false);
  };
  const saveReflection=async()=>{
    if(!reflectionText.trim()||savingReflection) return;
    setSavingReflection(true);
    try{
      const usedPrompt=promptShown?currentPrompt:null;
      await api.reflections.save(submittedMood,null,reflectionText.trim(),usedPrompt);
      const date=new Date().toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});
      onAddJournal({date,mood:submittedMood,text:reflectionText.trim(),prompt:usedPrompt});
      setReflectionText("");
    }catch(err){ console.error(err); }
    setSavingReflection(false);
  };

  return(
    <div>
      <div style={{background:C.orange,padding:"18px 20px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:C.white,fontSize:22,fontWeight:700,letterSpacing:"-.3px"}}>My Insights</div>
            <div style={{color:"rgba(255,255,255,.6)",fontSize:12,fontWeight:500,marginTop:2}}>Private — visible only to you</div>
          </div>
          <Chip label="Private" color={C.white} bg="rgba(255,255,255,.2)"/>
        </div>
      </div>
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",gap:10}}>
          {[
            {label:"Avg mood",val:avg},
            {label:"Best day",val:bestV?(showEmojis?me(bestV):ml(bestV)):"—",
              large:!bestV||showEmojis,color:bestV&&!showEmojis?mc(bestV,monoColors):C.txtDk},
            {label:"Check-ins",val:`${valid.length} / ${timeframe}`},
          ].map(s=>(
            <div key={s.label} style={{flex:1,background:C.white,borderRadius:14,padding:"14px 8px",
              textAlign:"center",border:`1px solid ${C.creamDk}`}}>
              <div style={{fontWeight:700,fontSize:s.large?28:22,color:s.color||C.txtDk,lineHeight:1}}>{s.val}</div>
              <div style={{fontSize:10,color:C.txtLt,fontWeight:600,marginTop:6,
                textTransform:"uppercase",letterSpacing:.5}}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{background:C.white,borderRadius:14,padding:16,border:`1px solid ${C.creamDk}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:13,color:C.txtDk,
              textTransform:"uppercase",letterSpacing:.6}}>Mood history</div>
            <div style={{display:"flex",gap:4}}>
              {TIMEFRAMES.map(tf=>(
                <button key={tf.days} onClick={()=>setTimeframe(tf.days)} style={{
                  padding:"4px 9px",borderRadius:20,border:"none",fontFamily:"inherit",
                  fontSize:10,fontWeight:700,letterSpacing:.3,cursor:"pointer",
                  background:timeframe===tf.days?C.orange:"transparent",
                  color:timeframe===tf.days?C.white:C.txtLt}}>
                  {tf.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",height:110,gap:timeframe===30?2:5}}>
            {history.map((v,i)=>{
              const showLabel=timeframe<=14||(timeframe===30&&(i===0||days[i].date===1||days[i].date%7===1));
              return(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",
                  alignItems:"center",justifyContent:"flex-end",height:"100%"}}>
                  <div style={{width:"100%",borderRadius:"4px 4px 0 0",
                    height:v?`${Math.round((v/5)*90)}px`:"3px",
                    background:v?mc(v,monoColors):C.creamDk,transition:"height .4s"}}/>
                  <span style={{fontSize:9,color:C.txtLt,fontWeight:700,marginTop:5,letterSpacing:.3,
                    visibility:showLabel?"visible":"hidden"}}>
                    {timeframe===30?days[i].date:days[i].letter}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",
          color:C.txtMd,marginTop:4}}>Good moments</div>
        {highNotes.length===0?(
          <div style={{background:C.white,borderRadius:14,padding:"14px 16px",border:`1px solid ${C.creamDk}`}}>
            {addingMemory?(
              <>
                <div style={{fontSize:12,color:C.txtMd,fontWeight:500,marginBottom:10,lineHeight:1.6}}>
                  What makes you smile? A memory, a place, a feeling or a person.
                </div>
                <textarea className="troop-reflect" value={memoryText}
                  onChange={e=>setMemoryText(e.target.value)} autoFocus rows={3}
                  placeholder="Write something to come back to..."
                  style={{width:"100%",padding:"12px 14px",boxSizing:"border-box",resize:"none",
                    border:`1.5px solid ${C.creamDk}`,borderRadius:12,background:C.cream,
                    fontSize:13,color:C.txtDk,fontFamily:"inherit",outline:"none",lineHeight:1.6}}/>
                <button onClick={saveMemory} disabled={!memoryText.trim()||savingMemory}
                  style={{marginTop:10,width:"100%",padding:11,borderRadius:10,border:"none",
                    background:C.orange,color:C.white,fontWeight:700,fontSize:13,
                    cursor:"pointer",fontFamily:"inherit",opacity:!memoryText.trim()||savingMemory?.6:1}}>
                  {savingMemory?"Saving…":"Save"}
                </button>
                <button onClick={()=>{setAddingMemory(false);setMemoryText("");}}
                  style={{marginTop:6,width:"100%",padding:9,borderRadius:10,border:"none",
                    background:"transparent",color:C.txtLt,fontWeight:600,fontSize:12,
                    cursor:"pointer",fontFamily:"inherit"}}>
                  Cancel
                </button>
              </>
            ):(
              <>
                <div style={{fontSize:12,color:C.txtMd,fontWeight:500,marginBottom:12,lineHeight:1.6}}>
                  Leave a note for next time — what makes you smile? A memory, a place, a feeling or a person.
                </div>
                <button onClick={()=>setAddingMemory(true)}
                  style={{width:"100%",padding:"10px 0",borderRadius:10,
                    border:`1.5px solid ${C.creamDk}`,background:"transparent",
                    color:C.orange,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
                  Add a note
                </button>
              </>
            )}
          </div>
        ):(
          <div style={{background:C.white,borderRadius:14,padding:"14px 16px",border:`1px solid ${C.creamDk}`}}>
            {moment?(
              <>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,alignItems:"center"}}>
                  <span style={{fontSize:11,color:C.txtLt,fontWeight:600,letterSpacing:.2}}>{moment.date}</span>
                  {showEmojis
                    ?<span style={{fontSize:16}}>{me(moment.mood)}</span>
                    :<Chip label={ml(moment.mood)} color={mc(moment.mood,monoColors)} bg={`${mc(moment.mood,monoColors)}1A`}/>}
                </div>
                <div style={{fontSize:13,color:C.txtDk,lineHeight:1.65,marginBottom:12}}>{moment.text}</div>
              </>
            ):(
              <div style={{fontSize:12,color:C.txtMd,fontWeight:500,marginBottom:12,lineHeight:1.6}}>
                Look back on a moment when things felt good.
              </div>
            )}
            <button onClick={pickMoment} style={{width:"100%",padding:"10px 0",borderRadius:10,
              border:`1.5px solid ${C.creamDk}`,background:"transparent",
              color:C.orange,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
              {moment?"Another one":"Show me one"}
            </button>
            {moment&&(addingMemory?(
              <div style={{marginTop:12}}>
                <textarea className="troop-reflect" value={memoryText}
                  onChange={e=>setMemoryText(e.target.value)} autoFocus rows={3}
                  placeholder="Write something to come back to..."
                  style={{width:"100%",padding:"12px 14px",boxSizing:"border-box",resize:"none",
                    border:`1.5px solid ${C.creamDk}`,borderRadius:12,background:C.cream,
                    fontSize:13,color:C.txtDk,fontFamily:"inherit",outline:"none",lineHeight:1.6}}/>
                <button onClick={saveMemory} disabled={!memoryText.trim()||savingMemory}
                  style={{marginTop:8,width:"100%",padding:11,borderRadius:10,border:"none",
                    background:C.orange,color:C.white,fontWeight:700,fontSize:13,
                    cursor:"pointer",fontFamily:"inherit",opacity:!memoryText.trim()||savingMemory?.6:1}}>
                  {savingMemory?"Saving…":"Save"}
                </button>
                <button onClick={()=>{setAddingMemory(false);setMemoryText("");}}
                  style={{marginTop:6,width:"100%",padding:9,borderRadius:10,border:"none",
                    background:"transparent",color:C.txtLt,fontWeight:600,fontSize:12,
                    cursor:"pointer",fontFamily:"inherit"}}>
                  Cancel
                </button>
              </div>
            ):(
              <button onClick={()=>setAddingMemory(true)}
                style={{marginTop:8,width:"100%",padding:"9px 0",borderRadius:10,
                  border:`1.5px solid ${C.creamDk}`,background:"transparent",
                  color:C.txtMd,fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:"inherit",letterSpacing:.1}}>
                Add a note
              </button>
            ))}
          </div>
        )}

        <div style={{fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",
          color:C.txtMd,marginTop:4}}>Reflections</div>

        <div style={{background:C.white,borderRadius:14,padding:"14px 16px",border:`1px solid ${C.creamDk}`}}>

          {prompts&&(promptShown?(
            <div style={{background:C.cream,borderRadius:12,padding:"12px 14px",marginBottom:12,
              border:`1px solid ${C.creamDk}`}}>
              <div style={{fontSize:13,color:C.txtDk,lineHeight:1.7,fontWeight:500}}>
                {currentPrompt}
              </div>
              <button onClick={nextPrompt}
                style={{marginTop:8,background:C.orange,border:"none",borderRadius:20,
                  padding:"6px 12px",color:C.white,fontSize:11,fontWeight:700,cursor:"pointer",
                  fontFamily:"inherit",letterSpacing:.2}}>
                Try a different one?
              </button>
            </div>
          ):(
            <button onClick={()=>setPromptShown(true)}
              style={{width:"100%",marginBottom:12,padding:"9px 0",borderRadius:10,
                border:`1.5px solid ${C.creamDk}`,background:"transparent",
                color:C.txtMd,fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:"inherit",letterSpacing:.1}}>
              Want a prompt?
            </button>
          ))}

          <textarea className="troop-reflect" value={reflectionText}
            onChange={e=>setReflectionText(e.target.value)}
            placeholder="Write here..."
            style={{width:"100%",minHeight:90,padding:"12px 14px",boxSizing:"border-box",resize:"none",
              border:`1.5px solid ${C.creamDk}`,borderRadius:12,background:C.cream,
              fontSize:13,color:C.txtDk,fontFamily:"inherit",outline:"none",lineHeight:1.6}}/>

          {submittedMood===1&&(
            <div style={{fontSize:11,color:C.txtLt,lineHeight:1.6,marginTop:10,fontWeight:500}}>
              Journaling here is for reflection, not treatment. If things feel heavy for more than a few days, it might help to talk to someone — a GP, a friend, or Lifeline on 13 11 14.
            </div>
          )}

          <button onClick={saveReflection} disabled={!reflectionText.trim()||savingReflection}
            style={{marginTop:10,width:"100%",padding:11,borderRadius:10,border:"none",
              background:reflectionText.trim()?C.orange:"rgba(0,0,0,.08)",
              color:reflectionText.trim()?C.white:C.txtLt,
              fontWeight:700,fontSize:13,cursor:reflectionText.trim()&&!savingReflection?"pointer":"default",
              fontFamily:"inherit",opacity:savingReflection?.6:1}}>
            {savingReflection?"Saving…":"Save note"}
          </button>
        </div>

        {journal.filter(j=>!j.mood||j.mood<4).map((j,idx)=>(
          <div key={idx} style={{background:C.white,borderRadius:14,padding:"14px 16px",
            border:`1px solid ${C.creamDk}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:j.prompt?6:8}}>
              <span style={{fontSize:11,color:C.txtLt,fontWeight:600,letterSpacing:.2}}>{j.date}</span>
              {j.mood&&(showEmojis
                ?<span style={{fontSize:14}}>{me(j.mood)}</span>
                :<Chip label={ml(j.mood)} color={mc(j.mood,monoColors)} bg={`${mc(j.mood,monoColors)}1A`}/>)}
            </div>
            {j.prompt&&(
              <div style={{fontSize:11,color:C.txtLt,fontStyle:"italic",lineHeight:1.5,marginBottom:6}}>
                {j.prompt}
              </div>
            )}
            <div style={{fontSize:13,color:C.txtDk,lineHeight:1.65}}>{j.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Help ─────────────────────────────────────────────────────────────────────────
const SERVICES=[
  {name:"Lifeline",           desc:"24/7 crisis support & suicide prevention",        number:"13 11 14",     tel:"131114",    chat:"https://www.lifeline.org.au/crisis-chat/"},
  {name:"Beyond Blue",        desc:"Anxiety, depression & mental health support",      number:"1300 22 4636", tel:"1300224636",chat:"https://www.beyondblue.org.au/get-support/talk-to-a-counsellor"},
  {name:"Suicide Call Back",  desc:"Free counselling for people affected by suicide",  number:"1300 659 467", tel:"1300659467",chat:"https://www.suicidecallbackservice.org.au"},
  {name:"Kids Helpline",      desc:"Support for people aged 5–25",                    number:"1800 55 1800", tel:"1800551800", chat:"https://kidshelpline.com.au/get-help/webchat-counselling"},
  {name:"MensLine Australia", desc:"Support for men with emotional health concerns",   number:"1300 78 99 78",tel:"1300789978",chat:"https://mensline.org.au/phone-and-online-counselling"},
];

function HelpScreen({onStartBreathing}){
  return(
    <div>
      <div style={{background:C.orange,padding:"18px 20px 20px"}}>
        <div style={{color:C.white,fontSize:22,fontWeight:700,letterSpacing:"-.3px"}}>Need Help?</div>
        <div style={{color:"rgba(255,255,255,.7)",fontSize:13,marginTop:4,fontWeight:500,lineHeight:1.6}}>
          Reaching out takes courage. These services are free, confidential, and available now.
        </div>
      </div>
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
        <div style={{background:"#C0392B",borderRadius:14,padding:"14px 16px",
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:C.white,fontWeight:700,fontSize:15}}>In immediate danger?</div>
            <div style={{color:"rgba(255,255,255,.75)",fontSize:12,marginTop:2,fontWeight:500}}>Police, ambulance, fire</div>
          </div>
          <a href="tel:000" style={{background:C.white,color:"#C0392B",borderRadius:20,
            padding:"9px 18px",fontWeight:700,fontSize:16,textDecoration:"none",letterSpacing:.5}}>
            000
          </a>
        </div>
        <div style={{background:C.white,borderRadius:14,padding:"14px 16px",
          border:`1px solid ${C.creamDk}`,display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(207,64,8,.1)",
            flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:20,height:20,borderRadius:"50%",background:C.orange}}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>Box breathing</div>
            <div style={{fontSize:11,color:C.txtLt,fontWeight:500,marginTop:2,lineHeight:1.5}}>
              A simple technique to slow your nervous system
            </div>
          </div>
          <button onClick={onStartBreathing}
            style={{background:C.orange,color:C.white,border:"none",borderRadius:20,
              padding:"7px 14px",fontSize:11,fontWeight:700,cursor:"pointer",
              fontFamily:"inherit",flexShrink:0,letterSpacing:.2}}>
            Begin
          </button>
        </div>
        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.8,marginTop:4}}>
          Crisis & Support Lines
        </div>
        {SERVICES.map(s=>(
          <div key={s.name} style={{background:C.white,borderRadius:14,padding:"14px 16px",border:`1px solid ${C.creamDk}`}}>
            <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>{s.name}</div>
            <div style={{fontSize:12,color:C.txtMd,fontWeight:500,marginTop:2,marginBottom:12,lineHeight:1.5}}>{s.desc}</div>
            <div style={{display:"flex",gap:8}}>
              <a href={`tel:${s.tel}`} style={{flex:1,background:C.orange,color:C.white,borderRadius:10,
                padding:"9px 0",fontWeight:700,fontSize:13,textDecoration:"none",textAlign:"center"}}>
                Call {s.number}
              </a>
              <a href={s.chat} target="_blank" rel="noreferrer" style={{flex:1,background:C.cream,
                color:C.orange,borderRadius:10,padding:"9px 0",fontWeight:700,fontSize:13,
                textDecoration:"none",textAlign:"center",border:`1.5px solid ${C.creamDk}`}}>
                Online chat
              </a>
            </div>
          </div>
        ))}
        <div style={{padding:"12px 14px",borderRadius:12,background:"rgba(0,0,0,.035)",
          border:`1px solid ${C.creamDk}`,marginTop:4,textAlign:"center"}}>
          <div style={{fontSize:11,color:C.txtMd,fontWeight:500,lineHeight:1.6}}>
            All services are free and confidential.<br/>You don't have to be in crisis to call.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Troop ────────────────────────────────────────────────────────────────────────
const SHARE_TEXT="Join me on Troop - a simple way to keep an eye on each other.";

function InviteTab({active,onClick,children}){
  return(
    <div onClick={onClick} style={{flex:1,textAlign:"center",padding:"9px 0",borderRadius:10,
      cursor:"pointer",fontWeight:700,fontSize:12,letterSpacing:.2,
      background:active?C.orange:"transparent",color:active?C.white:C.txtMd}}>
      {children}
    </div>
  );
}
function InviteModal({onClose}){
  const [mode,setMode]=useState("link"); // "email" | "link"
  const [email,setEmail]=useState("");
  const [status,setStatus]=useState(null); // null | "sending" | "sent" | {error}
  const [link,setLink]=useState(null);     // {code,url}
  const [linkErr,setLinkErr]=useState(null);
  const [copied,setCopied]=useState(false);

  const sendInvite=async()=>{
    if(!email.trim()||status==="sending") return;
    setStatus("sending");
    try{
      await api.troop.invite(email.trim());
      setStatus("sent");
    }catch(err){
      setStatus({error:err.message});
    }
  };

  useEffect(()=>{
    if(mode!=="link"||link||linkErr) return;
    api.troop.inviteLink().then(setLink).catch(err=>setLinkErr(err.message));
  },[mode,link,linkErr]);

  const copyText=async(text)=>{
    try{
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(()=>setCopied(false),1800);
    }catch{ /* clipboard permission denied — Share button still works */ }
  };

  const shareLink=()=>{
    if(!link) return;
    if(navigator.share){
      navigator.share({
        title:"Join my troop",
        text:SHARE_TEXT,
        url:link.url,
      }).catch(()=>{});
    }else{
      copyText(`${SHARE_TEXT} ${link.url}`);
    }
  };

  const canShare=typeof navigator!=="undefined"&&!!navigator.share;

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        {status==="sent"?(
          <>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{width:40,height:40,borderRadius:12,background:C.greenBg,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>✓</div>
              <div style={{fontWeight:700,fontSize:16,color:C.txtDk,lineHeight:1.2}}>Invite sent</div>
            </div>
            <div style={{fontSize:13,color:C.txtMd,lineHeight:1.7,marginBottom:18}}>
              {email.trim()} will show up in your troop once they accept the request.
            </div>
            <button onClick={onClose} style={{width:"100%",padding:13,
              borderRadius:12,border:"none",background:C.orange,color:C.white,
              fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
              Done
            </button>
          </>
        ):(
          <>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{width:40,height:40,borderRadius:12,background:C.amberBg,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>+</div>
              <div>
                <div style={{fontWeight:700,fontSize:16,color:C.txtDk,lineHeight:1.2}}>Invite to your troop</div>
                <div style={{fontSize:11,color:C.txtMd,marginTop:1}}>Bring someone into your circle</div>
              </div>
            </div>

            <div style={{display:"flex",gap:4,background:C.cream,borderRadius:12,padding:4,marginBottom:18}}>
              <InviteTab active={mode==="email"} onClick={()=>setMode("email")}>By email</InviteTab>
              <InviteTab active={mode==="link"} onClick={()=>setMode("link")}>Share a link</InviteTab>
            </div>

            {mode==="email"?(
              <>
                <div style={{fontSize:12,color:C.txtMd,lineHeight:1.6,marginBottom:12}}>
                  They'll need an existing Troop account — this sends a request they can accept.
                </div>
                <input className="troop-reflect" type="email" value={email} autoFocus
                  onChange={e=>{setEmail(e.target.value);setStatus(null);}}
                  onKeyDown={e=>{if(e.key==="Enter") sendInvite();}}
                  placeholder="their@email.com"
                  style={{width:"100%",padding:"12px 14px",boxSizing:"border-box",
                    border:`1.5px solid ${status?.error?"#E05050":C.creamDk}`,borderRadius:12,
                    background:C.cream,fontSize:13,color:C.txtDk,fontFamily:"inherit",outline:"none"}}/>
                {status?.error&&
                  <div style={{fontSize:12,color:"#E05050",fontWeight:600,marginTop:8}}>{status.error}</div>}
                <button onClick={sendInvite} disabled={status==="sending"} style={{marginTop:12,width:"100%",padding:13,
                  borderRadius:12,border:"none",background:C.orange,color:C.white,
                  fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2,
                  opacity:status==="sending"?.6:1}}>
                  {status==="sending"?"Sending…":"Send invite"}
                </button>
              </>
            ):(
              <>
                <div style={{fontSize:12,color:C.txtMd,lineHeight:1.6,marginBottom:12}}>
                  Anyone with this link joins your troop instantly — perfect for friends who don't have Troop yet. Send it however you'd text them.
                </div>
                {linkErr?(
                  <div style={{fontSize:12,color:"#E05050",fontWeight:600,marginBottom:12}}>{linkErr}</div>
                ):!link?(
                  <div style={{fontSize:12,color:C.txtMd,padding:"14px 0",textAlign:"center"}}>Generating your link…</div>
                ):(
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 14px",borderRadius:12,
                    border:`1.5px solid ${C.creamDk}`,background:C.cream,marginBottom:12,boxSizing:"border-box"}}>
                    <div style={{flex:1,fontSize:12,color:C.txtDk,fontWeight:600,overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{link.url}</div>
                    <button onClick={()=>copyText(link.url)} style={{flexShrink:0,border:"none",borderRadius:20,
                      padding:"6px 12px",fontSize:11,fontWeight:700,letterSpacing:.2,cursor:"pointer",
                      fontFamily:"inherit",background:copied?C.greenBg:C.creamDk,
                      color:copied?C.green:C.txtMd}}>
                      {copied?"Copied":"Copy"}
                    </button>
                  </div>
                )}
                <button onClick={shareLink} disabled={!link} style={{width:"100%",padding:13,
                  borderRadius:12,border:"none",background:C.orange,color:C.white,
                  fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2,
                  opacity:link?1:.6}}>
                  {canShare?"Share via text or app":(copied?"Copied — paste it anywhere":"Copy link to share")}
                </button>
              </>
            )}

            <button onClick={onClose} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
              border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
              fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              {mode==="email"?"Cancel":"Close"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
function EditProfileModal({profile,onSave,onClose}){
  const [name,setName]=useState(profile?.display_name??"");
  const [status,setStatus]=useState(null); // null | "saving" | {error}

  const save=async()=>{
    const trimmed=name.trim();
    if(!trimmed||status==="saving") return;
    if(trimmed===profile?.display_name){ onClose(); return; }
    setStatus("saving");
    try{
      const updated=await api.profile.update({display_name:trimmed});
      onSave(updated);
      onClose();
    }catch(err){
      setStatus({error:err.message});
    }
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
        maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
        <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{width:40,height:40,borderRadius:12,background:C.amberBg,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>✎</div>
          <div>
            <div style={{fontWeight:700,fontSize:16,color:C.txtDk,lineHeight:1.2}}>Edit profile</div>
            <div style={{fontSize:11,color:C.txtMd,marginTop:1}}>This is shown to your troop too</div>
          </div>
        </div>

        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",
          letterSpacing:.6,marginBottom:8}}>Display name</div>
        <input className="troop-reflect" type="text" value={name} autoFocus
          onChange={e=>{setName(e.target.value);setStatus(null);}}
          onKeyDown={e=>{if(e.key==="Enter") save();}}
          placeholder="Your name"
          style={{width:"100%",padding:"12px 14px",boxSizing:"border-box",
            border:`1.5px solid ${status?.error?"#E05050":C.creamDk}`,borderRadius:12,
            background:C.cream,fontSize:13,color:C.txtDk,fontFamily:"inherit",outline:"none"}}/>
        {status?.error&&
          <div style={{fontSize:12,color:"#E05050",fontWeight:600,marginTop:8}}>{status.error}</div>}

        <button onClick={save} disabled={status==="saving"||!name.trim()} style={{marginTop:12,width:"100%",padding:13,
          borderRadius:12,border:"none",background:C.orange,color:C.white,
          fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit",letterSpacing:.2,
          opacity:status==="saving"||!name.trim()?.6:1}}>
          {status==="saving"?"Saving…":"Save"}
        </button>
        <button onClick={onClose} style={{marginTop:8,width:"100%",padding:11,borderRadius:12,
          border:"none",background:"transparent",color:C.txtMd,fontWeight:600,
          fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
          Cancel
        </button>
      </div>
    </div>
  );
}
function TroopScreen({pinned,members,pending,reached,openReach,doPin,doUnpin,doAccept,monoColors}){
  useTick(60000);
  const [inviting,setInviting]=useState(false);
  const [pinError,setPinError]=useState("");
  return(
    <div>
      <div style={{background:C.orange,padding:"18px 20px 20px"}}>
        <div style={{color:C.white,fontSize:22,fontWeight:700,letterSpacing:"-.3px"}}>Your Troop</div>
        <div style={{color:"rgba(255,255,255,.6)",fontSize:12,marginTop:2,fontWeight:500}}>
          {members.length} {members.length===1?"person":"people"} in your troop
        </div>
      </div>
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
        <button onClick={()=>setInviting(true)} style={{width:"100%",padding:13,borderRadius:12,
          border:"none",background:C.orange,color:C.white,fontWeight:700,fontSize:14,
          cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
          + Invite someone to your troop
        </button>

        <div style={{background:C.amberBg,borderRadius:12,padding:"12px 14px",
          border:`1px solid rgba(224,112,16,.2)`}}>
          <div style={{fontWeight:700,fontSize:11,color:C.amber,marginBottom:4,
            textTransform:"uppercase",letterSpacing:.6}}>How privacy works</div>
          <div style={{fontSize:12,color:C.txtMd,lineHeight:1.65,fontWeight:500}}>
            You see check-in status only — never anyone's mood score or history. An alert appears only when the system detects an unusual pattern over time, not a single difficult day.
          </div>
        </div>

        {pinError&&<CapErrorModal message={pinError} onClose={()=>setPinError("")}/>}

        {pending.length>0&&(
          <>
            <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",
              letterSpacing:.6,marginTop:4}}>Requests</div>
            {pending.map(r=>(
              <div key={r.id} style={{background:C.white,borderRadius:13,padding:"12px 14px",
                display:"flex",alignItems:"center",gap:10,border:`1.5px solid ${C.orange}33`,
                boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>
                <Avi i={r.profile?.avatar_initial} bc={monoColors?monoColorFor(r.requester_id):colorFor(r.requester_id)} dark={monoColors} sz={38}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>{r.profile?.display_name||"Someone"}</div>
                  <div style={{fontSize:11,color:C.txtMd,marginTop:1}}>Wants to join your troop</div>
                </div>
                <button onClick={()=>doAccept(r.requester_id)}
                  style={{background:C.orange,color:C.white,border:"none",borderRadius:20,
                    padding:"7px 13px",fontSize:11,fontWeight:700,cursor:"pointer",
                    fontFamily:"inherit",flexShrink:0,letterSpacing:.2}}>
                  Accept
                </button>
              </div>
            ))}
          </>
        )}

        {pinned.length>0&&(
          <>
            <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",
              letterSpacing:.6,marginTop:4}}>You watch</div>
            {pinned.map(f=>(
              <div key={f.id} style={{background:C.white,borderRadius:14,padding:14,
                border:`1px solid ${f.anomaly_type?"rgba(224,112,16,.3)":C.creamDk}`,
                boxShadow:"0 1px 6px rgba(0,0,0,.05)"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <Avi i={f.avatar_initial} bc={monoColors?monoColorFor(f.id):colorFor(f.id)} dark={monoColors} sz={42}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:15,color:C.txtDk,letterSpacing:"-.1px"}}>{f.display_name}</div>
                    {f.anomaly_type&&
                      <div style={{fontSize:11,color:C.amber,fontWeight:600,marginTop:2}}>
                        {f.anomaly_type==='rough'?'Having a tough day':f.anomaly_type==='sustained_low'?'Sustained low':'Unusual pattern detected'} · {timeAgo(f.last_checkin_at)}
                      </div>}
                  </div>
                  {f.anomaly_type&&!reached[f.id]&&(
                    <button onClick={()=>openReach(f)} style={{background:C.orange,color:C.white,
                      border:"none",borderRadius:20,padding:"7px 13px",fontSize:11,fontWeight:700,
                      cursor:"pointer",fontFamily:"inherit",flexShrink:0,letterSpacing:.2}}>
                      Check in
                    </button>
                  )}
                  {reached[f.id]&&<Chip label="Done" color={C.green} bg={C.greenBg}/>}
                  {!f.anomaly_type&&!reached[f.id]&&(
                    <button onClick={()=>doUnpin(f.id)} style={{background:"transparent",color:C.txtLt,
                      border:`1px solid ${C.creamDk}`,borderRadius:20,padding:"5px 10px",fontSize:11,
                      fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                      Unwatch
                    </button>
                  )}
                </div>
                {f.anomaly_type&&(
                  <div style={{marginTop:10,padding:"9px 12px",borderRadius:10,background:C.amberBg,
                    fontSize:12,color:C.txtMd,fontWeight:500,lineHeight:1.6}}>
                    {f.anomaly_type==='sustained_low'
                      ?`${f.display_name.split(' ')[0]} has had a difficult stretch lately. A simple message could mean a lot — no need to mention this.`
                      :`${f.display_name.split(' ')[0]}'s pattern has shifted lately. A simple message could mean a lot — no need to mention this alert.`}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {members.length>0&&(
          <>
            <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",
              letterSpacing:.6,marginTop:4}}>All Troop</div>
            {members.map(m=>(
              <div key={m.id} style={{background:C.white,borderRadius:13,padding:"12px 14px",
                display:"flex",alignItems:"center",gap:10,border:`1px solid ${C.creamDk}`,
                boxShadow:"0 1px 4px rgba(0,0,0,.04)"}}>
                <Avi i={m.avatar_initial} bc={monoColors?monoColorFor(m.id):colorFor(m.id)} dark={monoColors} sz={38}/>
                <div style={{flex:1,fontWeight:600,fontSize:14,color:C.txtDk}}>{m.display_name}</div>
                <button onClick={()=>m.is_pinned?doUnpin(m.id):doPin(m.id).catch(err=>setPinError(err.message))}
                  style={{background:m.is_pinned?C.orange:"transparent",
                    color:m.is_pinned?C.white:C.txtMd,
                    border:`1.5px solid ${m.is_pinned?C.orange:C.creamDk}`,
                    borderRadius:20,padding:"6px 13px",fontSize:11,fontWeight:700,
                    cursor:"pointer",fontFamily:"inherit",flexShrink:0,letterSpacing:.2,
                    transition:"all .12s"}}>
                  {m.is_pinned?"Watching":"Watch"}
                </button>
              </div>
            ))}
          </>
        )}

      </div>
      {inviting&&<InviteModal onClose={()=>setInviting(false)}/>}
    </div>
  );
}

// ── Settings ────────────────────────────────────────────────────────────────────
function TestNotifButton(){
  const [status,setStatus]=useState(null);
  const send=async()=>{
    setStatus("sending");
    try{ await api.push.test(); setStatus("sent"); }
    catch(e){ setStatus("error"); }
    setTimeout(()=>setStatus(null),3000);
  };
  return(
    <div onClick={status?undefined:send}
      style={{background:"transparent",borderRadius:13,padding:"12px 16px",
        display:"flex",justifyContent:"center",alignItems:"center",
        border:`1.5px dashed ${C.creamDk}`,cursor:status?"default":"pointer"}}>
      <span style={{fontSize:12,color:status==="sent"?C.green:status==="error"?"#C62828":C.txtLt,fontWeight:500}}>
        {status==="sending"?"Sending…":status==="sent"?"Notification sent":"Send test notification (testing only)"}
      </span>
    </div>
  );
}

function SettingsScreen({members,doNominate,doDenominate,thresholdMsg,dismissThreshold,sensitivity,setSensitivity,showEmojis,setShowEmojis,monoColors,setMonoColors,profile,onProfileUpdate,onResetData,onReplayOnboarding}){
  const [nominateError,setNominateError]=useState("");
  const [editingProfile,setEditingProfile]=useState(false);
  const descriptions={
    Low:"Only flags severe, sustained drops across multiple days.",
    Medium:"Flags noticeable dips or a few tough days in a row.",
    High:"Flags any significant shift from your recent baseline.",
  };
  const handleSignOut=()=>supabase.auth.signOut();

  const [notifEnabled,setNotifEnabled]=useState(false);
  const [notifError,setNotifError]=useState("");
  const [showNotifPrompt,setShowNotifPrompt]=useState(false);

  useEffect(()=>{
    if(!pushSupported()) return;
    navigator.serviceWorker.getRegistration()
      .then(reg=>reg?.pushManager.getSubscription())
      .then(sub=>{
        setNotifEnabled(!!sub);
        if(sub) api.push.subscribe(sub.toJSON()).catch(()=>{});
      })
      .catch(()=>{});
  },[]);

  const enableNotifications=async()=>{
    setShowNotifPrompt(false);
    setNotifError("");
    try{
      await subscribeToPush();
      setNotifEnabled(true);
      await api.settings.update({notifications_enabled:true});
    }catch(err){
      setNotifError(err.message);
    }
  };
  const toggleNotifications=async()=>{
    if(!notifEnabled){ setShowNotifPrompt(true); return; }
    setNotifError("");
    try{
      await unsubscribeFromPush();
      setNotifEnabled(false);
      await api.settings.update({notifications_enabled:false});
    }catch(err){
      setNotifError(err.message);
    }
  };

  return(
    <div>
      <div style={{background:C.orange,padding:"18px 20px 20px"}}>
        <div style={{color:C.white,fontSize:22,fontWeight:700,letterSpacing:"-.3px"}}>Settings</div>
      </div>
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.8}}>
          Detection sensitivity
        </div>
        <div style={{background:C.white,borderRadius:14,padding:14,border:`1px solid ${C.creamDk}`}}>
          <div style={{fontSize:12,color:C.txtMd,fontWeight:500,marginBottom:12,lineHeight:1.6}}>
            Controls when the app flags an unusual pattern, prompts a personal reflection, and notifies your troop.
          </div>
          <div style={{display:"flex",gap:7,marginBottom:10}}>
            {["Low","Medium","High"].map(s=>(
              <div key={s} onClick={()=>setSensitivity(s)} style={{
                flex:1,padding:"10px 6px",borderRadius:10,textAlign:"center",cursor:"pointer",
                background:sensitivity===s?C.orange:C.cream,
                color:sensitivity===s?C.white:C.txtMd,
                fontWeight:700,fontSize:12,letterSpacing:.2,
                border:`1.5px solid ${sensitivity===s?C.orange:C.creamDk}`,transition:"all .12s"}}>
                {s}
              </div>
            ))}
          </div>
          <div style={{fontSize:12,color:C.txtLt,fontWeight:500,fontStyle:"italic"}}>
            {descriptions[sensitivity]}
          </div>
        </div>

        <div style={{height:2}}/>
        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.8}}>
          Notifications
        </div>
        <div style={{background:C.white,borderRadius:13,padding:"14px 16px",
          display:"flex",alignItems:"center",gap:12,border:`1px solid ${C.creamDk}`}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>Push notifications</div>
            <div style={{fontSize:10,color:C.txtLt,fontWeight:600,marginTop:1}}>
              {notifEnabled?"On for this device":"Get notified when your troop reaches out"}
            </div>
            {notifError&&(
              <div style={{fontSize:10,color:"#C62828",fontWeight:600,marginTop:4,lineHeight:1.5}}>
                {notifError}
                {isIOS()&&!isStandalone()&&" On iPhone, add Troop to your Home Screen first."}
              </div>
            )}
          </div>
          <Toggle on={notifEnabled} onToggle={toggleNotifications}/>
        </div>

        <div style={{height:2}}/>
        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.8}}>
          Appearance
        </div>
        <div style={{background:C.white,borderRadius:13,padding:"14px 16px",
          display:"flex",alignItems:"center",gap:12,border:`1px solid ${C.creamDk}`}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>Mood emojis</div>
            <div style={{fontSize:10,color:C.txtLt,fontWeight:600,marginTop:1}}>
              {showEmojis?"Showing emojis for moods":"Showing colors and labels instead"}
            </div>
          </div>
          <Toggle on={showEmojis} onToggle={()=>setShowEmojis(!showEmojis)}/>
        </div>
        <div style={{background:C.white,borderRadius:13,padding:"14px 16px",
          display:"flex",alignItems:"center",gap:12,border:`1px solid ${C.creamDk}`}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>Muted color scale</div>
            <div style={{fontSize:10,color:C.txtLt,fontWeight:600,marginTop:1}}>
              {monoColors?"Using muted tones for moods and troop":"Using full colors for moods and troop"}
            </div>
          </div>
          <Toggle on={monoColors} onToggle={()=>setMonoColors(!monoColors)}/>
        </div>

        <div style={{height:2}}/>
        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.8}}>
          Watch you
        </div>
        {thresholdMsg&&(
          <div style={{background:C.amberBg,borderRadius:12,padding:"12px 14px",
            border:`1px solid rgba(224,112,16,.3)`,display:"flex",alignItems:"flex-start",gap:10}}>
            <div style={{flex:1,fontSize:12,color:C.txtMd,fontWeight:500,lineHeight:1.65}}>
              Your troop has grown — some members may receive fewer notifications about you.
            </div>
            <button onClick={dismissThreshold} style={{background:"none",border:"none",cursor:"pointer",
              color:C.txtLt,fontSize:16,lineHeight:1,padding:"0 0 0 4px",flexShrink:0}}>✕</button>
          </div>
        )}
        <div style={{background:C.amberBg,borderRadius:12,padding:"10px 14px",
          border:`1px solid rgba(224,112,16,.2)`,marginBottom:2}}>
          <div style={{fontSize:11,color:C.txtMd,fontWeight:500,lineHeight:1.6}}>
            These people receive a gentle nudge when a pattern is detected. They will <strong>never</strong> see your mood score or history.
          </div>
        </div>
        {nominateError&&<CapErrorModal message={nominateError} onClose={()=>setNominateError("")}/>}
        {members.map(m=>(
          <div key={m.id} style={{background:C.white,borderRadius:13,padding:"12px 16px",
            display:"flex",alignItems:"center",gap:12,border:`1px solid ${C.creamDk}`}}>
            <Avi i={m.avatar_initial} bc={monoColors?monoColorFor(m.id):colorFor(m.id)} dark={monoColors} sz={36}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:C.txtDk}}>{m.display_name}</div>
              <div style={{fontSize:10,color:C.txtLt,fontWeight:600,marginTop:1}}>
                {m.is_nominated?"Will be notified":"Won't be notified"}
              </div>
            </div>
            <Toggle on={!!m.is_nominated} onToggle={()=>m.is_nominated?doDenominate(m.id):doNominate(m.id).catch(err=>setNominateError(err.message))}/>
          </div>
        ))}

        <div style={{height:2}}/>
        <div style={{fontSize:11,fontWeight:700,color:C.txtLt,textTransform:"uppercase",letterSpacing:.8}}>Account</div>
        {["Edit profile","Privacy policy"].map(item=>(
          <div key={item} onClick={()=>{
            if(item==="Edit profile") setEditingProfile(true);
            if(item==="Privacy policy") window.open("/privacy.html","_blank");
          }}
            style={{background:C.white,borderRadius:13,padding:"14px 16px",
            display:"flex",justifyContent:"space-between",alignItems:"center",
            border:`1px solid ${C.creamDk}`,cursor:"pointer"}}>
            <span style={{fontWeight:600,fontSize:14,color:C.txtDk}}>{item}</span>
            <span style={{color:C.txtLt,fontSize:18,lineHeight:1}}>›</span>
          </div>
        ))}
        <div onClick={onResetData} style={{background:C.white,borderRadius:13,padding:"14px 16px",
          display:"flex",justifyContent:"space-between",alignItems:"center",
          border:`1px solid ${C.creamDk}`,cursor:"pointer"}}>
          <span style={{fontWeight:600,fontSize:14,color:"#C62828"}}>Reset all my data</span>
          <span style={{color:C.txtLt,fontSize:18,lineHeight:1}}>›</span>
        </div>
        <div onClick={handleSignOut} style={{background:C.white,borderRadius:13,padding:"14px 16px",
          display:"flex",justifyContent:"space-between",alignItems:"center",
          border:`1px solid ${C.creamDk}`,cursor:"pointer"}}>
          <span style={{fontWeight:600,fontSize:14,color:"#C62828"}}>Sign out</span>
        </div>
        <div onClick={onReplayOnboarding} style={{background:"transparent",borderRadius:13,
          padding:"12px 16px",display:"flex",justifyContent:"center",alignItems:"center",
          border:`1.5px dashed ${C.creamDk}`,cursor:"pointer",marginTop:8}}>
          <span style={{fontSize:12,color:C.txtLt,fontWeight:500}}>Replay onboarding (testing only)</span>
        </div>
        <TestNotifButton/>
      </div>
      {editingProfile&&
        <EditProfileModal profile={profile} onSave={onProfileUpdate} onClose={()=>setEditingProfile(false)}/>}
      {showNotifPrompt&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
          display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
          <div style={{background:C.white,borderRadius:"20px 20px 0 0",padding:"24px 20px 40px",
            maxWidth:400,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.15)"}}>
            <div style={{width:32,height:3,borderRadius:2,background:C.creamDk,margin:"0 auto 24px"}}/>
            <div style={{fontWeight:700,fontSize:18,color:C.txtDk,letterSpacing:"-.3px",marginBottom:12}}>
              Stay in the loop
            </div>
            <div style={{fontSize:14,color:C.txtMd,fontWeight:500,lineHeight:1.7,marginBottom:20}}>
              Your troop is keeping an eye out for you. Notifications mean you'll know when someone reaches out, when a friend might need a quiet check-in, and you'll get a daily nudge to keep your own streak going.
            </div>
            <div style={{fontSize:13,color:C.txtLt,fontWeight:500,lineHeight:1.6,marginBottom:20}}>
              Turn them on — it only takes a second, and it might matter more than you think.
            </div>
            <button onClick={enableNotifications}
              style={{width:"100%",padding:13,borderRadius:12,border:"none",
                background:C.orange,color:C.white,fontWeight:700,fontSize:14,
                cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
              Turn on notifications
            </button>
            <button onClick={()=>setShowNotifPrompt(false)}
              style={{marginTop:8,width:"100%",padding:11,borderRadius:12,border:"none",
                background:"transparent",color:C.txtLt,fontWeight:500,fontSize:13,
                cursor:"pointer",fontFamily:"inherit"}}>
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Nav ─────────────────────────────────────────────────────────────────────────
function NavBar({tab,setTab}){
  const items=[
    {id:"home",    lbl:"Home",
      svg:<svg width="20" height="20" viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"/></svg>},
    {id:"insights",lbl:"Insights",
      svg:<svg width="20" height="20" viewBox="0 0 24 24"><path d="M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6z" fill="currentColor"/></svg>},
    {id:"troop",   lbl:"Troop",
      svg:<svg width="20" height="20" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/></svg>},
    {id:"help",    lbl:"Help",
      svg:<svg width="20" height="20" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/></svg>},
    {id:"settings",lbl:"Settings",
      svg:<svg width="20" height="20" viewBox="0 0 24 24"><path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor"/></svg>},
  ];
  return(
    <div style={{display:"flex",background:C.white,borderTop:`1px solid ${C.creamDk}`,
      paddingBottom:"env(safe-area-inset-bottom)",flexShrink:0}}>
      {items.map(t=>{
        const active=tab===t.id;
        return(
          <div key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"10px 0 9px",
            display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer",
            color:active?C.orange:C.txtLt}}>
            {t.svg}
            <span style={{fontSize:10,fontWeight:active?700:600,letterSpacing:.3,
              color:active?C.orange:C.txtLt,textTransform:"uppercase"}}>{t.lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Onboarding ───────────────────────────────────────────────────────────────────
const ONBOARD_MOOD_SCALE=[
  {v:1,lbl:"Rough", desc:"Really struggling. It's a hard day and things feel heavy.", c:"#E05050"},
  {v:2,lbl:"Down",  desc:"A bit flat or low. Not quite yourself.",                   c:"#E08030"},
  {v:3,lbl:"Okay",  desc:"Getting through it. Neither good nor bad.",                c:"#E0B800"},
  {v:4,lbl:"Good",  desc:"Feeling good. Things are on track.",                       c:"#80C030"},
  {v:5,lbl:"Great", desc:"Really well. Energised or something good is happening.",   c:"#30A860"},
];

function OnboardingScreen({onComplete}){
  const notifAvailable=pushSupported();
  const SLIDES=[
    'welcome','checkin','insights','troop','whoinvite','alerts','goodtoknow',
    ...(notifAvailable?['notifications']:[]),
  ];
  const [slide,setSlide]=useState(0);
  const [notifBusy,setNotifBusy]=useState(false);
  const current=SLIDES[slide];
  const isLast=slide===SLIDES.length-1;

  const next=()=>{ if(isLast) onComplete(); else setSlide(s=>s+1); };

  const handleEnableNotif=async()=>{
    setNotifBusy(true);
    try{
      const sub=await subscribeToPush();
      if(sub) await api.settings.update({notifications_enabled:true});
    }catch(e){ console.error(e); }
    setNotifBusy(false);
    next();
  };

  const CONTENT={
    welcome:{
      label:"Welcome",
      title:"A quiet eye on the people you care about",
      body:"Troop is a short daily check-in designed to remove the barriers to checking in on yourself and the people you care about. No app replaces actually knowing someone — Troop just makes it easier to notice.",
      disclaimer:"Troop is a tool to support connection, not replace it. Whatever this app tells you, checking in on the people you care about in person — a call, a text, a coffee — always matters. Don't wait for an alert to reach out.",
    },
    checkin:{
      label:"Daily check-in",
      title:"Habits are formed by consistency",
      body:"Just a number, once a day. The more regularly you check in, the more meaningful the picture becomes — for you and the people watching out for you. Your mood is always private; your troop only ever sees that you showed up.",
    },
    insights:{
      label:"Insights",
      title:"Understand yourself over time",
      body:"The Insights tab shows your mood history and tracks your check-in streak — giving you a private space to reflect.",
      bullets:[
        "Journal prompts help you put words to how you are feeling",
        "Capture good moments to look back on",
        "Everything here is completely private — only you can see it",
      ],
    },
    troop:{
      label:"Your troop",
      title:"Keep it small and trusted",
      body:"Invite the people you would want to know if you were going through a rough patch.",
      bullets:[
        "Pin someone to quietly watch their check-in pattern",
        "Nominate people to be notified if your own pattern shifts",
        "A few trusted people is all you need",
      ],
    },
    whoinvite:{
      label:"Who to invite",
      title:"Invite the people who notice",
      body:"Research on peer support shows that what matters most is not how many people you have around you — it is whether those people tend to act. A small group who genuinely pay attention will always outperform a long list of people who mean well.",
      bullets:[
        "Think of who you would call after a genuinely hard month",
        "Someone who has noticed before when you were not quite yourself",
        "Someone whose call you would actually pick up on a hard day",
        "Someone who would reach out — not just think about it",
        "You do not need to explain why you invited them — just send the invite",
      ],
    },
    alerts:{
      label:"How alerts work",
      title:"Meaningful alerts, not noise",
      body:"When the app notices an unusual pattern, it quietly nudges the right people to check in. One message at the right moment can mean everything.",
      bullets:[
        "Only people you have pinned can trigger an alert for you",
        "A single tough day never triggers an alert — patterns over time only",
        "Once notified about someone, you won't be prompted again for 48 hours",
        "A smaller troop means alerts stay meaningful, not overwhelming",
        "Go to Settings to nominate who watches you and adjust alert sensitivity",
      ],
    },
    goodtoknow:{
      label:"Good to know",
      title:"A few more things worth knowing",
      body:"Troop has a few rules that help it stay safe and useful.",
      bullets:[
        "If nobody reaches out within 24 hours of an alert, a second wave of people gets quietly notified",
        "You can pin someone without them knowing — watching is always one-sided unless they pin you back",
        "You can update your check-in once a day if things change after you log",
        "You can only reach out to the same person once per hour",
        "If someone has nominated you, or has very few people watching them, you may be nudged to check in even if you have not pinned them",
        "The Help tab has box breathing and crisis support lines — always there when you need them",
      ],
    },
    notifications:{
      label:"Notifications",
      title:"Stay in the loop",
      body:"Your troop is keeping an eye out for you. Notifications mean you will know when someone reaches out, when a friend might need a quiet check-in, and you will get a daily nudge to keep your streak going.",
      sub:"Turn them on — it only takes a second, and it might matter more than you think.",
    },
  };

  const c=CONTENT[current];

  return(
    <div style={{position:"fixed",top:0,bottom:0,left:"50%",transform:"translateX(-50%)",
      width:"100%",maxWidth:400,display:"flex",flexDirection:"column",
      background:C.cream,fontFamily:"'Inter',system-ui,sans-serif",
      boxShadow:"0 0 60px rgba(0,0,0,.15)",zIndex:500}}>

      <div style={{background:C.orange,padding:"20px 20px 28px",
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{color:C.white,fontSize:13,fontWeight:700,
          letterSpacing:".12em",textTransform:"uppercase"}}>Troop</div>
        <button onClick={onComplete} style={{background:"none",border:"none",
          color:"rgba(255,255,255,.55)",fontSize:13,fontWeight:600,
          cursor:"pointer",fontFamily:"inherit",padding:0}}>Skip</button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"32px 24px 16px"}}>
        <div style={{fontSize:11,fontWeight:700,color:C.orange,textTransform:"uppercase",
          letterSpacing:".1em",marginBottom:12}}>{c.label}</div>
        <div style={{fontSize:22,fontWeight:700,color:C.txtDk,letterSpacing:"-.4px",
          lineHeight:1.28,marginBottom:14}}>{c.title}</div>
        <div style={{fontSize:14,color:C.txtMd,fontWeight:500,lineHeight:1.75,marginBottom:c.disclaimer||c.bullets?18:0}}>
          {c.body}
        </div>

        {c.disclaimer&&(
          <div style={{background:C.white,borderRadius:12,padding:"12px 14px",marginBottom:c.bullets?18:0,
            border:`1.5px solid ${C.creamDk}`,borderLeft:`3px solid ${C.orange}`}}>
            <div style={{fontSize:12,color:C.txtMd,lineHeight:1.7,fontWeight:500}}>
              {c.disclaimer}
            </div>
          </div>
        )}

        {current==='checkin'&&(
          <div style={{marginTop:18,display:"flex",flexDirection:"column",gap:1}}>
            {ONBOARD_MOOD_SCALE.map(m=>(
              <div key={m.v} style={{display:"flex",alignItems:"center",gap:12,
                padding:"10px 12px",borderRadius:10,background:C.white,
                border:`1px solid ${C.creamDk}`,marginBottom:4}}>
                <div style={{width:10,height:10,borderRadius:"50%",
                  background:m.c,flexShrink:0}}/>
                <div style={{fontWeight:700,fontSize:13,color:C.txtDk,width:42,flexShrink:0}}>{m.lbl}</div>
                <div style={{fontSize:12,color:C.txtMd,fontWeight:500,lineHeight:1.5}}>{m.desc}</div>
              </div>
            ))}
          </div>
        )}

        {c.bullets&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {c.bullets.map((b,i)=>(
              <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:C.orange,
                  flexShrink:0,marginTop:5,opacity:.6}}/>
                <div style={{fontSize:13,color:C.txtMd,fontWeight:500,lineHeight:1.6}}>{b}</div>
              </div>
            ))}
          </div>
        )}

        {c.sub&&(
          <div style={{marginTop:14,fontSize:13,color:C.txtLt,fontWeight:500,lineHeight:1.65}}>
            {c.sub}
          </div>
        )}
      </div>

      <div style={{padding:"10px 24px 10px",display:"flex",justifyContent:"center",gap:6}}>
        {SLIDES.map((_,i)=>(
          <div key={i} style={{height:6,borderRadius:3,
            background:i===slide?C.orange:C.creamDk,
            width:i===slide?22:6,
            transition:"all .25s"}}/>
        ))}
      </div>

      <div style={{padding:"8px 24px 48px",display:"flex",flexDirection:"column",gap:8}}>
        {current==='notifications'?(
          <>
            <button onClick={handleEnableNotif} disabled={notifBusy}
              style={{width:"100%",padding:14,borderRadius:12,border:"none",
                background:C.orange,color:C.white,fontWeight:700,fontSize:15,
                cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
              Turn on notifications
            </button>
            <button onClick={next} disabled={notifBusy}
              style={{width:"100%",padding:12,borderRadius:12,border:"none",
                background:"transparent",color:C.txtLt,fontWeight:500,fontSize:13,
                cursor:"pointer",fontFamily:"inherit"}}>
              Maybe later
            </button>
          </>
        ):(
          <button onClick={next}
            style={{width:"100%",padding:14,borderRadius:12,border:"none",
              background:C.orange,color:C.white,fontWeight:700,fontSize:15,
              cursor:"pointer",fontFamily:"inherit",letterSpacing:.2}}>
            {isLast?"Get started":"Next"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────────
export default function TroopApp(){
  const [session,setSession]=useState(null);
  const [authLoading,setAuthLoading]=useState(true);
  const [tab,setTab]=useState("home");
  const [mood,setMood]=useState(null);
  const [note,setNote]=useState("");
  const [checkedIn,setCheckedIn]=useState(false);
  const [submittedMood,setSubmitted]=useState(null);
  const [myHistory,setMyHistory]=useState(Array(7).fill(null));
  const [allCheckins,setAllCheckins]=useState([]);
  const [streak,setStreak]=useState(0);
  const [pinned,setPinned]=useState([]);
  const [members,setMembers]=useState([]);
  const [thresholdMsg,setThresholdMsg]=useState(false);
  const [profile,setProfile]=useState(null);
  const [reached,setReached]=useState({});
  const [reachInbox,setReachInbox]=useState([]);
  const [reachTarget,setReachTarget]=useState(null);
  const [showResetModal,setShowResetModal]=useState(false);
  const [showBreathing,setShowBreathing]=useState(false);
  const [showCelebration,setShowCelebration]=useState(false);
  const [pending,setPending]=useState([]);
  const [journal,setJournal]=useState([]);
  const [anomaly,setAnomaly]=useState(null);
  const [pendingAlert,setPendingAlert]=useState(null);
  const [alertSent,setAlertSent]=useState(false);
  const [editingCheckin,setEditingCheckin]=useState(false);
  const [hasUpdatedToday,setHasUpdatedToday]=useState(()=>!!localStorage.getItem(`troop_updated_${localDateStr()}`));
  const [onboarded,setOnboarded]=useState(()=>!!localStorage.getItem('troop_onboarded'));
  const [showPostOnboardInvite,setShowPostOnboardInvite]=useState(false);
  const [sensitivity,setSensitivity]=useState("Medium");
  const [showEmojis,setShowEmojisState]=useState(true);
  const [monoColors,setMonoColorsState]=useState(false);

  // Auth — onAuthStateChange fires INITIAL_SESSION after URL code exchange is done,
  // so we never flash the login screen after an OAuth redirect
  useEffect(()=>{
    const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      console.log('[auth]', event, session?.user?.email ?? 'no user');
      setSession(session);
      setAuthLoading(false);
    });
    return()=>subscription.unsubscribe();
  },[]);

  // Capture ?invite=<code> from a shared link — stash it through the OAuth
  // redirect (which drops the query string) so it can be redeemed once signed in
  useEffect(()=>{
    const code=new URLSearchParams(window.location.search).get("invite");
    if(code){
      localStorage.setItem(PENDING_INVITE_KEY,code);
      window.history.replaceState({},"",window.location.pathname);
    }
  },[]);

  // Load data
  useEffect(()=>{
    if(!session) return;
    Promise.all([
      api.me(),
      api.troop.list(),
      api.checkins.history(90),
      api.reflections.list(),
      api.troop.pending(),
      api.troop.reachInbox(),
    ]).then(([me,troopData,historyData,reflectionsData,pendingData,inboxData])=>{
      setProfile(me);
      setSensitivity(me.settings?.sensitivity||"Medium");
      setShowEmojisState(me.settings?.show_emojis ?? true);
      setMonoColorsState(me.settings?.mono_colors ?? false);
      setPinned(troopData.pinned);
      setMembers(troopData.members);
      setPending(pendingData||[]);
      setReachInbox(inboxData||[]);
      setMyHistory(buildWeekHistory(historyData));
      setAllCheckins(historyData);
      setStreak(computeStreak(historyData));
      setJournal(reflectionsData.map(r=>({
        date:new Date(r.created_at).toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"}),
        mood:r.mood,
        text:r.body,
        prompt:r.prompt||null,
      })));
      const todayStr=localDateStr();
      const todayCheckin=historyData.find(c=>c.checked_in_on===todayStr);
      if(todayCheckin){setCheckedIn(true);setSubmitted(todayCheckin.mood);}

      const pendingCode=localStorage.getItem(PENDING_INVITE_KEY);
      if(pendingCode){
        localStorage.removeItem(PENDING_INVITE_KEY);
        api.troop.redeem(pendingCode)
          .then(()=>api.troop.list())
          .then(({pinned:p,members:m})=>{ setPinned(p); setMembers(m); })
          .catch(err=>console.error('[invite redeem]',err));
      }

      // If the user had notifications enabled but cleared app data, the browser
      // subscription is gone while the OS permission is still granted — re-subscribe
      // silently so they don't lose notifications without realising.
      if(me.settings?.notifications_enabled && pushSupported() &&
         typeof Notification!=='undefined' && Notification.permission==='granted'){
        navigator.serviceWorker.getRegistration()
          .then(reg=>reg?.pushManager.getSubscription())
          .then(sub=>{ if(!sub) subscribeToPush().catch(()=>{}); })
          .catch(()=>{});
      }
    }).catch(console.error);
  },[session]);

  useEffect(()=>{
    const link=document.createElement("link");
    link.rel="stylesheet";
    link.href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    const style=document.createElement("style");
    style.textContent=`
      .troop-input::placeholder{color:rgba(255,255,255,.45)}
      .troop-input{color:#fff;font-family:inherit}
      .troop-reflect::placeholder{color:#BBA080}
      *{-webkit-font-smoothing:antialiased}
    `;
    document.head.appendChild(style);
    return()=>{link.remove();style.remove();};
  },[]);

  const doCheckin=async()=>{
    if(!mood) return;
    try{
      const {checkin,anomaly:detected}=await api.checkins.log(mood,note||undefined,localDateStr());
      setSubmitted(checkin.mood);
      setMyHistory(h=>{const n=[...h];n[6]=mood;return n;});
      setStreak(s=>s+1);
      setCheckedIn(true);
      setEditingCheckin(false);
      if(mood===1) setShowBreathing(true);
      if(detected) setAnomaly(detected);
      else if(mood<=2) setAnomaly({type:"low_mood"});
      else if(mood===5) setAnomaly({type:"great_mood"});
      else setAlertSent(false);
    }catch(err){
      if(err.message==="Already checked in today"){setCheckedIn(true);setSubmitted(mood);}
      else console.error(err);
    }
  };

  const doUpdateCheckin=async()=>{
    if(!mood) return;
    try{
      const {checkin,anomaly:detected}=await api.checkins.update(mood,note||undefined,localDateStr());
      setSubmitted(checkin.mood);
      setMyHistory(h=>{const n=[...h];n[6]=mood;return n;});
      setEditingCheckin(false);
      localStorage.setItem(`troop_updated_${localDateStr()}`,'1');
      setHasUpdatedToday(true);
      if(mood===1) setShowBreathing(true);
      if(detected) setAnomaly(detected);
      else if(mood<=2) setAnomaly({type:"low_mood"});
      else if(mood===5) setAnomaly({type:"great_mood"});
      else setAlertSent(false);
    }catch(err){ console.error(err); }
  };

  const handleSave=async(text)=>{
    const a=anomaly;
    const wasGreat=a.type==="great_mood";
    const wasAnomaly=!PERSONAL_PROMPT_TYPES.includes(a.type);
    try{ await api.reflections.save(submittedMood,a.type,text); }
    catch(err){ console.error(err); }
    const today=new Date().toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});
    setJournal(j=>[{date:today,mood:submittedMood,text},...j]);
    setAnomaly(null);
    if(wasAnomaly) setPendingAlert(a);
    else if(wasGreat) setShowCelebration(true);
  };

  const handleSkip=()=>{
    const a=anomaly;
    const wasGreat=a.type==="great_mood";
    const wasAnomaly=!PERSONAL_PROMPT_TYPES.includes(a.type);
    setAnomaly(null);
    if(wasAnomaly) setPendingAlert(a);
    else if(wasGreat) setShowCelebration(true);
  };

  const confirmAlert=()=>{
    const type=pendingAlert.type;
    setPendingAlert(null);
    setAlertSent(true);
    api.checkins.alert(type).catch(err=>console.error('[alert]',err));
  };

  const declineAlert=()=>{
    setPendingAlert(null);
    setAlertSent(false);
  };

  const updateSensitivity=async(val)=>{
    setSensitivity(val);
    try{ await api.settings.update({sensitivity:val}); }catch(err){ console.error(err); }
  };

  const updateShowEmojis=async(val)=>{
    setShowEmojisState(val);
    try{ await api.settings.update({show_emojis:val}); }catch(err){ console.error(err); }
  };

  const updateMonoColors=async(val)=>{
    setMonoColorsState(val);
    try{ await api.settings.update({mono_colors:val}); }catch(err){ console.error(err); }
  };

  const doReach=async(id,message)=>{
    setReached(r=>({...r,[id]:true}));
    try{ await api.troop.reach(id,message); }catch(err){
      setReached(r=>({...r,[id]:false}));
      throw err;
    }
  };
  const doAccept=async(requesterId)=>{
    try{
      await api.troop.accept(requesterId);
      setPending(p=>p.filter(r=>r.requester_id!==requesterId));
      const {pinned:p2,members:m2}=await api.troop.list();
      setPinned(p2); setMembers(m2);
    }catch(err){ console.error(err); }
  };
  const openReach=f=>setReachTarget(f);
  const ackReach=async(id,reply)=>{
    setReachInbox(inbox=>inbox.filter(r=>r.id!==id));
    try{ await api.troop.ackReach(id,reply); }
    catch(err){ console.error('[ack reach]',err); }
  };
  const clearMoodCache=async()=>{
    try{
      await Promise.all([api.checkins.deleteAll(), api.reflections.deleteAll()]);
    }catch(err){ console.error(err); }
    Object.keys(localStorage).filter(k=>k.startsWith("troop_updated_")).forEach(k=>localStorage.removeItem(k));
    setHasUpdatedToday(false);
    setCheckedIn(false);
    setSubmitted(null);
    setMood(null);
    setNote("");
    setMyHistory(Array(7).fill(null));
    setStreak(0);
    setJournal([]);
    setShowCelebration(false);
    setShowBreathing(false);
    setReachTarget(null);
    setAnomaly(null);
    setAlertSent(false);
    setEditingCheckin(false);
  };

  const refreshTroop=async()=>{
    const {pinned:p,members:m}=await api.troop.list();
    setPinned(p); setMembers(m);
  };
  const doPin=async(id)=>{ try{await api.troop.pin(id);await refreshTroop();}catch(err){console.error(err);throw err;} };
  const doUnpin=async(id)=>{ try{await api.troop.unpin(id);await refreshTroop();}catch(err){console.error(err);} };
  const doNominate=async(id)=>{
    setMembers(m=>m.map(x=>x.id===id?{...x,is_nominated:true}:x));
    try{
      const result=await api.troop.nominate(id);
      if(result?.threshold_message) setThresholdMsg(true);
    }catch(err){
      setMembers(m=>m.map(x=>x.id===id?{...x,is_nominated:false}:x));
      console.error(err);
      throw err;
    }
  };
  const doDenominate=async(id)=>{
    setMembers(m=>m.map(x=>x.id===id?{...x,is_nominated:false}:x));
    try{await api.troop.denominate(id);}catch(err){ setMembers(m=>m.map(x=>x.id===id?{...x,is_nominated:true}:x)); console.error(err); }
  };

  if(authLoading) return <LoadingScreen/>;
  if(!session)    return <AuthScreen/>;
  if(!onboarded)  return <OnboardingScreen onComplete={()=>{localStorage.setItem('troop_onboarded','1');setOnboarded(true);setTab('home');setShowPostOnboardInvite(true);}}/>;

  const shared={pinned,members,pending,reached,openReach,doPin,doUnpin,doAccept,doNominate,doDenominate,thresholdMsg,dismissThreshold:()=>setThresholdMsg(false),myHistory,allCheckins,journal,showEmojis,monoColors,streak,submittedMood};

  return(
    <div style={{position:"fixed",top:0,bottom:0,left:"50%",transform:"translateX(-50%)",
      width:"100%",maxWidth:400,display:"flex",flexDirection:"column",
      background:C.cream,fontFamily:"'Inter',system-ui,sans-serif",
      overflow:"hidden",boxShadow:"0 0 60px rgba(0,0,0,.15)"}}>
      <div style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
        {tab==="home"     &&<HomeScreen     {...shared} mood={mood} setMood={setMood} note={note} setNote={setNote} checkedIn={checkedIn} submittedMood={submittedMood} doCheckin={doCheckin} doUpdateCheckin={doUpdateCheckin} editingCheckin={editingCheckin} setEditingCheckin={setEditingCheckin} hasUpdatedToday={hasUpdatedToday} alertSent={alertSent} profile={profile} email={session?.user?.email} setTab={setTab} reachInbox={reachInbox} onAckReach={ackReach}/>}
        {tab==="insights" &&<InsightsScreen {...shared} onAddJournal={entry=>setJournal(j=>[entry,...j])}/>}
        {tab==="troop"    &&<TroopScreen    {...shared}/>}
        {tab==="help"     &&<HelpScreen onStartBreathing={()=>setShowBreathing(true)}/>}
        {tab==="settings" &&<SettingsScreen {...shared} sensitivity={sensitivity} setSensitivity={updateSensitivity} setShowEmojis={updateShowEmojis} setMonoColors={updateMonoColors} profile={profile} onProfileUpdate={updated=>setProfile(p=>({...p,...updated}))} onResetData={()=>setShowResetModal(true)} onReplayOnboarding={()=>{localStorage.removeItem('troop_onboarded');setOnboarded(false);}}/>}
      </div>
      <NavBar tab={tab} setTab={setTab}/>
      {anomaly&&!showBreathing&&<ReflectionModal anomaly={anomaly} onSave={handleSave} onSkip={handleSkip}/>}
      {pendingAlert&&<AlertConfirmModal anomaly={pendingAlert} onConfirm={confirmAlert} onDecline={declineAlert} onHelp={()=>{setPendingAlert(null);setTab("help");}}/>}
      {reachTarget&&<ReachModal friend={reachTarget} monoColors={monoColors} onSend={doReach} onClose={()=>setReachTarget(null)}/>}
      {showResetModal&&<ResetDataModal onConfirm={clearMoodCache} onClose={()=>setShowResetModal(false)}/>}
      {showBreathing&&<BreathingModal onClose={()=>setShowBreathing(false)}/>}
      {showCelebration&&<CelebrationModal pinned={pinned} members={members} monoColors={monoColors} onReach={f=>{setReachTarget(f);}} onClose={()=>setShowCelebration(false)}/>}
      {showPostOnboardInvite&&<InviteModal onClose={()=>setShowPostOnboardInvite(false)}/>}
    </div>
  );
}
